<template>
  <div>
    <!-- Leave Requests Table -->
    <b-card class="leave-requests-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0">{{ $t("Leave Requests") }}</h5>
          <small class="text-muted">{{
            $t("Manage and review leave requests")
          }}</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="4" lg="4" md="5" sm="6" cols="12">
          <b-form-input
            id="search"
            v-model="filters.search"
            :placeholder="$t('Search by name') + '...'"
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>

        <!-- Date Range Filter -->
        <b-col xl="4" lg="4" md="5" sm="12" cols="12">
          <div class="d-flex align-items-center gap-2">
            <flat-pickr
              v-model="filters.dateRange"
              :config="dateRangeConfig"
              class="form-control filter-control"
              :placeholder="$t('Date Range')"
              @on-change="onDateRangeChange"
            />
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              @click="clearFilters"
              class="clear-btn-circle"
              v-b-tooltip.hover.top="$t('Clear Filters')"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-col>

        <!-- Action Buttons -->
        <b-col xl="4" lg="4" md="12" sm="12" cols="12" class="text-right">
          <div class="d-flex gap-2 align-items-center justify-content-end">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddModal"
              class="action-btn-filter"
              :disabled="addLoading || approveLoading || rejectLoading"
            >
              <b-spinner v-if="addLoading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              {{ $t("Add Request") }}
            </b-button>
            <b-button
              v-if="rights.view"
              variant="outline-primary"
              @click="openPrintModal"
              class="print-btn-icon"
            >
              <feather-icon icon="PrinterIcon" size="16" />
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-3"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Pagination Switch -->
          <b-form-checkbox
            v-model="showPagination"
            switch
            size="sm"
            class="pagination-switch-compact"
          >
            <small class="text-muted">{{ $t("Pagination") }}</small>
          </b-form-checkbox>

          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              :key="'page-size-' + pageSize"
              v-model="pageSize"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalRecords }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div v-if="showPagination" class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="pageSize"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="leave-requests-table"
            :items="leaveRequests"
            :fields="fields"
            :busy="tableBusy"
            :responsive="false"
            hover
            show-empty
            :empty-text="tableEmptyText"
            @sort-changed="onSortChanged"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #head()="data">
              <span>{{ $t(data.label) }}</span>
            </template>

            <!-- Enhanced Name Cell with Avatar -->
            <template #cell(name)="{ item }">
              <div class="lr-person-cell">
                <b-avatar
                  size="40"
                  :src="item.picture"
                  variant="light-primary"
                  class="mr-1 shadow-sm"
                >
                  <feather-icon
                    v-if="!item.picture"
                    icon="UserIcon"
                    size="20"
                  />
                </b-avatar>
                <div class="lr-person-info">
                  <span class="lr-person-name">{{ item.name }}</span>
                  <span class="lr-person-sub">{{ item.designation || '' }}</span>
                </div>
              </div>
            </template>

            <template #cell(campus)="{ item }">
              <div class="lr-dept-cell">
                <feather-icon
                  icon="MapPinIcon"
                  size="12"
                  class="lr-cell-icon"
                />
                <span>{{ campusLabel(item) }}</span>
              </div>
            </template>

            <template #cell(designation)="{ item }">
              <b-badge variant="light-info" class="lr-badge-pill">
                {{ item.designation }}
              </b-badge>
            </template>

            <template #cell(department)="{ item }">
              <div class="lr-dept-cell">
                <feather-icon icon="BriefcaseIcon" size="12" class="lr-cell-icon" />
                <span>{{ item.department }}</span>
              </div>
            </template>

            <template #cell(leaveType)="{ item }">
              <div class="lr-type-cell">
                <feather-icon icon="TagIcon" size="12" class="lr-type-icon" />
                <span>{{ item.leaveType }}</span>
              </div>
            </template>

            <template #cell(dtFrom)="{ item }">
              <div class="lr-date-cell">
                <feather-icon icon="CalendarIcon" size="12" class="lr-date-icon" />
                <span>{{ formatDate(item.dtFrom) }}</span>
              </div>
            </template>

            <template #cell(dtTo)="{ item }">
              <div class="lr-date-cell">
                <feather-icon icon="CalendarIcon" size="12" class="lr-date-icon" />
                <span>{{ formatDate(item.dtTo) }}</span>
              </div>
            </template>

            <template #cell(totalDays)="{ item }">
              <div class="lr-days-chip">
                <feather-icon icon="SunIcon" size="11" class="mr-25" />
                {{ item.totalDays }} {{ $t("days") }}
              </div>
            </template>

            <template #cell(status)="{ item }">
              <b-badge :variant="getStatusVariant(item.status)" class="lr-status-badge">
                {{ item.status }}
              </b-badge>
            </template>

            <template #cell(leaveNote)="{ item }">
              <div v-if="item.note" class="note-cell-modern">
                <div
                  class="note-badge-modern leave-note-modern"
                  @click="openNoteModal(item.note, 'Leave Note', item)"
                >
                  <feather-icon
                    icon="MessageSquareIcon"
                    size="14"
                    class="note-icon"
                  />
                  <span class="note-text">{{
                    truncateText(item.note, 30)
                  }}</span>
                </div>
              </div>
              <span v-else class="text-muted-empty">-</span>
            </template>

            <template #cell(adminNote)="{ item }">
              <div v-if="item.exNote" class="note-cell-modern">
                <div
                  class="note-badge-modern admin-note-modern"
                  @click="openNoteModal(item.exNote, 'Admin Note', item)"
                >
                  <feather-icon
                    icon="UserCheckIcon"
                    size="14"
                    class="note-icon"
                  />
                  <span class="note-text">{{
                    truncateText(item.exNote, 30)
                  }}</span>
                </div>
              </div>
              <span v-else class="text-muted-empty">-</span>
            </template>

            <template #cell(attachment)="{ item }">
              <div v-if="item.attachment" class="attachment-cell-modern">
                <b-badge
                  variant="info"
                  pill
                  class="attachment-badge-modern"
                  @click="viewLeaveRequest(item)"
                >
                  <feather-icon icon="PaperclipIcon" size="12" class="mr-1" />
                  {{ getAttachmentCount(item.attachment) }}
                </b-badge>
              </div>
              <span v-else class="text-muted-empty">-</span>
            </template>

            <template #cell(sno)="{ index }">
              <span class="text-muted">{{
                (currentPage - 1) * pageSize + index + 1
              }}</span>
            </template>

            <template #cell(date)="{ item }">
              <b-badge variant="light-secondary">
                {{ formatDateTime(item.date) }}
              </b-badge>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  variant="gradient-primary"
                  v-b-tooltip.hover.left
                  :title="$t('View Details')"
                  size="sm"
                  class="action-btn-table mr-1"
                  @click="viewLeaveRequest(item)"
                  :disabled="
                    viewLoading === item.id ||
                    approveLoading ||
                    rejectLoading ||
                    addLoading
                  "
                >
                  <b-spinner v-if="viewLoading === item.id" small type="grow" />
                  <feather-icon v-else icon="EyeIcon" size="16" />
                </b-button>

                <b-button
                  v-if="
                    rights.edit &&
                    item.status &&
                    item.status.toLowerCase() === 'pending'
                  "
                  variant="outline-success"
                  v-b-tooltip.hover.top
                  :title="$t('Approve')"
                  size="sm"
                  class="action-btn-outline approve-btn mr-1"
                  @click="openApproveModal(item)"
                  :disabled="
                    viewLoading === item.id ||
                    approveLoading ||
                    rejectLoading ||
                    addLoading
                  "
                >
                  <b-spinner
                    v-if="
                      approveLoading &&
                      selectedActionItem &&
                      selectedActionItem.id === item.id
                    "
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="CheckIcon" size="16" />
                </b-button>

                <b-button
                  v-if="
                    rights.edit &&
                    item.status &&
                    item.status.toLowerCase() === 'pending'
                  "
                  variant="outline-danger"
                  v-b-tooltip.hover.right
                  :title="$t('Reject')"
                  size="sm"
                  class="action-btn-outline reject-btn"
                  @click="openRejectModal(item)"
                  :disabled="
                    viewLoading === item.id ||
                    approveLoading ||
                    rejectLoading ||
                    addLoading
                  "
                >
                  <b-spinner
                    v-if="
                      rejectLoading &&
                      selectedActionItem &&
                      selectedActionItem.id === item.id
                    "
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="XIcon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- View Leave Request Modal -->
    <b-modal
      v-model="showModal"
      hide-header
      @hidden="selectedLeaveRequest = null"
      centered
      no-close-on-backdrop
      size="lg"
      body-class="p-0"
      content-class="lr-modal-content"
    >
      <!-- Custom Gradient Header -->
      <div class="lr-modal-header">
        <div class="lr-modal-header-left">
          <div class="lr-modal-icon-wrap">
            <feather-icon icon="ClipboardIcon" size="20" />
          </div>
          <div>
            <h5 class="lr-modal-title mb-0">{{ $t('Leave Request Details') }}</h5>
            <small class="lr-modal-subtitle">{{ $t('Staff leave request record') }}</small>
          </div>
        </div>
        <b-button variant="flat-secondary" class="lr-modal-close-btn" @click="showModal = false">
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>
      <div v-if="selectedLeaveRequest" class="lr-modal-body">
        <!-- Employee Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="UserIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Employee Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("ID") }}</span>
              <span class="details-value details-value-primary">{{
                selectedLeaveRequest.tID
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Name") }}</span>
              <span class="details-value details-value-primary">{{
                selectedLeaveRequest.name
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Designation") }}</span>
              <span class="details-value details-value-info">{{
                selectedLeaveRequest.designation
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Department") }}</span>
              <span class="details-value details-value-info">{{
                selectedLeaveRequest.department
              }}</span>
            </div>
          </div>
        </div>

        <!-- Leave Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="CalendarIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Leave Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Type") }}</span>
              <span class="details-value">
                <b-badge variant="warning" class="details-badge">{{
                  selectedLeaveRequest.leaveType
                }}</b-badge>
              </span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Days") }}</span>
              <span class="details-value details-value-success">{{
                selectedLeaveRequest.totalDays
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("From") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedLeaveRequest.dtFrom)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("To") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedLeaveRequest.dtTo)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Status") }}</span>
              <span class="details-value">
                <b-badge
                  :variant="getStatusVariant(selectedLeaveRequest.status)"
                  class="details-badge text-capitalize"
                >
                  {{ selectedLeaveRequest.status }}
                </b-badge>
              </span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Request Date") }}</span>
              <span class="details-value details-value-muted">{{
                formatDateTime(selectedLeaveRequest.date)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Additional Information Section -->
        <div
          v-if="selectedLeaveRequest.note || selectedLeaveRequest.attachment"
          class="leave-details-section mb-3"
        >
          <div class="section-header">
            <feather-icon icon="InfoIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">
              {{ $t("Additional Information") }}
            </h6>
          </div>
          <div v-if="selectedLeaveRequest.note" class="details-note-item">
            <span class="details-label">{{ $t("Note") }}</span>
            <div class="details-note-content">
              {{ selectedLeaveRequest.note }}
            </div>
          </div>
          <div
            v-if="selectedLeaveRequest.attachment"
            class="details-attachment-item"
          >
            <span class="details-label">{{ $t("Attachment") }}</span>
            <div class="details-attachment-content">
              <div
                v-for="(attachment, index) in getAttachmentList(
                  selectedLeaveRequest.attachment
                )"
                :key="index"
                class="details-attachment-wrapper"
              >
                <div
                  v-if="isImageFile(attachment)"
                  class="details-attachment-image"
                >
                  <img
                    :src="attachment"
                    :alt="
                      $t('Attachment') +
                      ' ' +
                      (index + 1) +
                      ' ' +
                      $t('for') +
                      ' ' +
                      selectedLeaveRequest.name
                    "
                    class="details-attachment-preview"
                    @click="openImageLightbox(attachment)"
                  />
                </div>
                <div v-else class="details-attachment-file">
                  <div class="details-file-icon">
                    <feather-icon icon="FileIcon" size="18" />
                  </div>
                  <div class="details-file-info">
                    <p class="details-file-name">
                      {{ getFileName(attachment) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Approval/Rejection Information Section -->
        <div
          v-if="selectedLeaveRequest.status !== 'Pending'"
          class="leave-details-section"
        >
          <div class="section-header">
            <feather-icon
              :icon="
                selectedLeaveRequest.status === 'Approved'
                  ? 'CheckIcon'
                  : 'XIcon'
              "
              size="18"
              class="section-icon"
            />
            <h6 class="section-title mb-0">
              {{ selectedLeaveRequest.status }} {{ $t("Information") }}
            </h6>
          </div>
          <div
            v-if="selectedLeaveRequest.status === 'Approved'"
            class="details-meta-grid"
          >
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Approved From") }}</span>
              <span class="details-value details-value-success">{{
                formatDate(selectedLeaveRequest.dtApprovedFrom)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Approved To") }}</span>
              <span class="details-value details-value-success">{{
                formatDate(selectedLeaveRequest.dtApprovedTo)
              }}</span>
            </div>
          </div>
          <div v-if="selectedLeaveRequest.exNote" class="details-note-item">
            <span class="details-label"
              >{{
                selectedLeaveRequest.status === "Approved"
                  ? "Approval"
                  : "Rejection"
              }}
              {{ $t("Note") }}</span
            >
            <div class="details-note-content">
              {{ selectedLeaveRequest.exNote }}
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false" class="lr-modal-footer-btn">
          <feather-icon icon="XIcon" size="14" class="mr-50" />
          {{ $t("Close") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Image Modal -->
    <b-modal
      id="image-modal"
      v-model="showImageModal"
      :title="$t('Attachment Preview')"
      @hidden="selectedImageUrl = null"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <div v-if="selectedImageUrl" class="text-center">
        <img
          :src="selectedImageUrl"
          :alt="$t('Attachment Preview')"
          class="img-fluid"
          style="max-height: 70vh; max-width: 100%"
        />
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showImageModal = false">
          {{ $t("Close") }}
        </b-button>
        <a :href="selectedImageUrl" target="_blank" class="btn btn-primary">
          <feather-icon icon="ExternalLinkIcon" class="mr-1" />
          {{ $t("Open in New Tab") }}
        </a>
      </template>
    </b-modal>

    <!-- Approve Modal -->
    <b-modal
      id="approve-modal"
      v-model="showApproveModal"
      :title="$t('Approve Leave Request')"
      @hidden="resetApproveForm"
      @shown="onApproveModalShown"
      centered
      no-close-on-backdrop
      size="lg"
      class="approve-modal-compact"
    >
      <div v-if="selectedActionItem">
        <!-- Employee Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="UserIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Employee Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Name") }}</span>
              <span class="details-value details-value-primary">{{
                selectedActionItem.name
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Designation") }}</span>
              <span class="details-value details-value-info">{{
                selectedActionItem.designation
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Department") }}</span>
              <span class="details-value details-value-info">{{
                selectedActionItem.department
              }}</span>
            </div>
          </div>
        </div>

        <!-- Leave Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="CalendarIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Leave Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Original From Date") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedActionItem.dtFrom)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Original To Date") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedActionItem.dtTo)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Approval Form Section -->
        <div class="approval-form-section">
          <b-form @submit.prevent="submitApprove">
            <b-form-group
              :label="$t('Approved From Date')"
              label-for="dtApprovedFrom"
            >
              <flat-pickr
                id="dtApprovedFrom"
                v-model="approveForm.dtApprovedFrom"
                :config="approvedFromDateConfig"
                class="form-control modern-input"
                :placeholder="$t('Select approved from date')"
                required
                @on-change="onApprovedFromDateChange"
              />
            </b-form-group>

            <b-form-group
              :label="$t('Approved To Date')"
              label-for="dtApprovedTo"
            >
              <flat-pickr
                id="dtApprovedTo"
                v-model="approveForm.dtApprovedTo"
                :config="approvedToDateConfig"
                class="form-control modern-input"
                :placeholder="$t('Select approved to date')"
                required
              />
            </b-form-group>

            <b-form-group :label="$t('Approval Note')" label-for="exNote">
              <textarea
                id="exNote"
                v-model="approveForm.exNote"
                class="form-control modern-textarea"
                :placeholder="
                  $t('I am approving this leave request because') + '...'
                "
                rows="4"
                required
                :class="{
                  'is-invalid':
                    !approveForm.exNote ||
                    approveForm.exNote.trim().length === 0,
                }"
              ></textarea>
              <div
                v-if="
                  !approveForm.exNote || approveForm.exNote.trim().length === 0
                "
                class="invalid-feedback"
              >
                {{ $t("Please provide a reason for approval") }}
              </div>
              <small class="form-text text-muted">
                {{
                  $t("Please explain why you are approving this leave request")
                }}
              </small>
            </b-form-group>
          </b-form>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showApproveModal = false">
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="success"
          @click="submitApprove"
          :disabled="!isApproveFormValid || approveLoading"
        >
          <b-spinner v-if="approveLoading" small type="grow" />
          <span v-else>{{ $t("Approve Leave") }}</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Reject Modal -->
    <b-modal
      id="reject-modal"
      v-model="showRejectModal"
      :title="$t('Reject Leave Request')"
      @hidden="resetRejectForm"
      centered
      no-close-on-backdrop
      size="lg"
      class="reject-modal-compact"
    >
      <div v-if="selectedActionItem">
        <!-- Employee Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="UserIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Employee Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Name") }}</span>
              <span class="details-value details-value-primary">{{
                selectedActionItem.name
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Designation") }}</span>
              <span class="details-value details-value-info">{{
                selectedActionItem.designation
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Department") }}</span>
              <span class="details-value details-value-info">{{
                selectedActionItem.department
              }}</span>
            </div>
          </div>
        </div>

        <!-- Leave Information Section -->
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="CalendarIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Leave Information") }}</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("From Date") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedActionItem.dtFrom)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("To Date") }}</span>
              <span class="details-value details-value-primary">{{
                formatDate(selectedActionItem.dtTo)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Rejection Form Section -->
        <div class="approval-form-section">
          <b-form @submit.prevent="submitReject">
            <b-form-group :label="$t('Rejection Note')" label-for="rejectNote">
              <textarea
                id="rejectNote"
                v-model="rejectForm.exNote"
                class="form-control modern-textarea"
                :placeholder="
                  $t('I am rejecting this leave request because') + '...'
                "
                rows="6"
                required
                :class="{
                  'is-invalid':
                    !rejectForm.exNote || rejectForm.exNote.trim().length === 0,
                }"
              ></textarea>
              <div
                v-if="
                  !rejectForm.exNote || rejectForm.exNote.trim().length === 0
                "
                class="invalid-feedback"
              >
                {{ $t("Please provide a reason for rejection") }}
              </div>
              <small class="form-text text-muted">
                {{
                  $t("Please explain why you are rejecting this leave request")
                }}
              </small>
            </b-form-group>
          </b-form>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showRejectModal = false">
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="danger"
          @click="submitReject"
          :disabled="!isRejectFormValid || rejectLoading"
        >
          <b-spinner v-if="rejectLoading" small type="grow" />
          <span v-else>{{ $t("Reject Leave") }}</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Add Leave Request Modal -->
    <b-modal
      id="add-leave-modal"
      v-model="showAddModal"
      :title="$t('Add Leave Request')"
      @hidden="resetAddForm"
      centered
      no-close-on-backdrop
      size="lg"
      class="add-leave-modal"
    >
      <b-form @submit.prevent="submitAddLeave" class="modern-form">
        <div class="form-section">
          <h6 class="section-title">
            <feather-icon icon="UserIcon" size="16" class="mr-2" />
            {{ $t("Employee Information") }}
          </h6>
          <b-row class="form-row-modern">
            <b-col md="6" v-if="shouldShowCampusDropdown">
              <b-form-group
                :label="$t('Campus')"
                label-for="campusSelect"
                class="modern-form-group"
              >
                <v-select
                  id="campusSelect"
                  v-model="addForm.campusID"
                  :options="campusOptions"
                  :reduce="(option) => option.id"
                  label="branch"
                  :clearable="false"
                  :placeholder="$t('Select campus')"
                  class="modern-select"
                  @input="onAddModalCampusChange"
                />
              </b-form-group>
            </b-col>
            <b-col :md="shouldShowCampusDropdown ? '6' : '12'">
              <b-form-group
                :label="$t('Department')"
                label-for="departmentSelect"
                class="modern-form-group"
              >
                <v-select
                  id="departmentSelect"
                  v-model="addForm.departmentID"
                  :options="addModalDepartmentOptions"
                  :reduce="(option) => option.id"
                  label="name"
                  :clearable="true"
                  :placeholder="$t('Select department')"
                  class="modern-select"
                  @input="onAddModalDepartmentChange"
                  :disabled="
                    !addForm.campusID ||
                    addForm.campusID === 0 ||
                    addModalDepartmentLoading
                  "
                  :loading="addModalDepartmentLoading"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row-modern">
            <b-col md="12">
              <b-form-group
                :label="$t('Staff Member')"
                label-for="staffSelect"
                :invalid-feedback="addFormErrors.staffID"
                class="modern-form-group"
              >
                <v-select
                  id="staffSelect"
                  v-model="addForm.tID"
                  :options="addModalStaffOptions"
                  :reduce="(option) => option.id"
                  label="name"
                  :clearable="false"
                  :placeholder="$t('Select staff member')"
                  class="modern-select"
                  :loading="addModalStaffLoading"
                  :disabled="!addForm.departmentID || addModalStaffLoading"
                  :class="{ 'is-invalid': addFormErrors.staffID }"
                  @input="onStaffSelect"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form-section">
          <h6 class="section-title">
            <feather-icon icon="CalendarIcon" size="16" class="mr-2" />
            {{ $t("Leave Details") }}
          </h6>
          <b-row class="form-row-modern">
            <b-col :md="isDynamicLeaveBalance ? 12 : 6">
              <b-form-group
                :label="$t('Leave Type')"
                label-for="leaveType"
                :invalid-feedback="addFormErrors.leaveType"
                class="modern-form-group leave-type-field"
              >
                <!-- dynamic_leave_balance ON: balance table (after staff selected) -->
                <div
                  v-if="isDynamicLeaveBalance && !addForm.tID"
                  class="lt-picker lt-picker--placeholder"
                >
                  <feather-icon icon="UserIcon" size="18" class="lt-picker__ph-icon" />
                  <span>{{ $t("Select staff first to load leave balances") }}</span>
                </div>

                <div
                  v-else-if="isDynamicLeaveBalance && addForm.tID"
                  class="lt-picker"
                  :class="{ 'lt-picker--invalid': addFormErrors.leaveType }"
                >
                  <div class="lt-picker__hint">
                    <feather-icon icon="InfoIcon" size="14" />
                    <span>{{
                      $t(
                        "Choose a leave type below. Remaining (left) vs total allowance (right)."
                      )
                    }}</span>
                  </div>

                  <div
                    v-if="leaveTypesLoading"
                    class="lt-picker__loading"
                  >
                    <b-spinner small variant="primary" />
                    <span>{{ $t("Loading leave balances…") }}</span>
                  </div>
                  <div
                    v-else-if="!leaveTypeOptions.length"
                    class="lt-picker__empty"
                  >
                    {{ $t("No leave types found for this staff member") }}
                  </div>
                  <div v-else class="lt-picker__scroll">
                    <div class="lt-picker__head">
                      <span>{{ $t("Leave type") }}</span>
                      <span>{{ $t("Remaining") }}</span>
                      <span>{{ $t("Total") }}</span>
                      <span class="sr-only">{{ $t("Selected") }}</span>
                    </div>
                    <button
                      v-for="opt in leaveTypeOptions"
                      :key="opt.value"
                      type="button"
                      class="lt-picker__row"
                      :class="{
                        'lt-picker__row--active':
                          addForm.leaveType === opt.value,
                      }"
                      @click="selectLeaveTypeOption(opt)"
                    >
                      <span class="lt-picker__name">{{ opt.text }}</span>
                      <span class="lt-picker__cell lt-picker__cell--rem">
                        <span
                          class="lt-pill"
                          :class="
                            Number(opt.remaining) > 0
                              ? 'lt-pill--ok'
                              : 'lt-pill--zero'
                          "
                        >
                          {{ formatLeaveCount(opt.remaining) }}
                        </span>
                      </span>
                      <span class="lt-picker__cell lt-picker__cell--tot">
                        <span class="lt-pill lt-pill--total">{{
                          formatLeaveCount(opt.allowed)
                        }}</span>
                      </span>
                      <span class="lt-picker__cell lt-picker__cell--mark">
                        <span
                          v-if="addForm.leaveType === opt.value"
                          class="lt-picker__check-wrap"
                        >
                          <feather-icon icon="CheckIcon" size="14" />
                        </span>
                      </span>
                    </button>
                  </div>

                  <div
                    v-if="selectedLeaveTypeMeta && !leaveTypesLoading"
                    class="lt-picker__footer"
                  >
                    <div class="lt-picker__footer-title">
                      {{ $t("Selected leave type") }}
                    </div>
                    <div class="lt-picker__footer-name">
                      {{ selectedLeaveTypeMeta.text }}
                    </div>
                    <div class="lt-picker__footer-stats">
                      <div
                        class="lt-stat"
                        :class="
                          Number(selectedLeaveTypeMeta.remaining) > 0
                            ? 'lt-stat--ok'
                            : 'lt-stat--zero'
                        "
                      >
                        <span class="lt-stat__label">{{
                          $t("Remaining")
                        }}</span>
                        <span class="lt-stat__value">{{
                          formatLeaveCount(selectedLeaveTypeMeta.remaining)
                        }}</span>
                      </div>
                      <div class="lt-stat lt-stat--neutral">
                        <span class="lt-stat__label">{{ $t("Total") }}</span>
                        <span class="lt-stat__value">{{
                          formatLeaveCount(selectedLeaveTypeMeta.allowed)
                        }}</span>
                      </div>
                      <div class="lt-stat lt-stat--ratio">
                        <span class="lt-stat__label">{{
                          $t("Balance")
                        }}</span>
                        <span class="lt-stat__value">{{
                          leaveTypeRatioLabel(selectedLeaveTypeMeta)
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- dynamic_leave_balance OFF: standard dropdown -->
                <v-select
                  v-else-if="!isDynamicLeaveBalance"
                  id="leaveType"
                  :key="'lt-dd-' + leaveTypeSelectKey"
                  v-model="addForm.leaveTypeOption"
                  :options="leaveTypeOptions"
                  label="text"
                  :clearable="false"
                  :placeholder="$t('Select leave type')"
                  class="modern-select leave-type-dropdown"
                  :loading="leaveTypesLoading"
                  :disabled="leaveTypesLoading"
                  :class="{ 'is-invalid': addFormErrors.leaveType }"
                  @input="onLeaveTypeOptionSelect"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('Date Range')"
                label-for="dateRange"
                :invalid-feedback="addFormErrors.dateRange"
                class="modern-form-group"
              >
                <flat-pickr
                  id="dateRange"
                  v-model="addForm.dateRange"
                  :config="dateRangeConfig"
                  class="form-control modern-input"
                  :placeholder="$t('Select date range')"
                  :class="{ 'is-invalid': addFormErrors.dateRange }"
                  @on-change="onAddFormDateRangeChange"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row-modern">
            <b-col md="6">
              <b-form-group
                :label="$t('Total Days')"
                label-for="totalDays"
                class="modern-form-group"
              >
                <b-form-input
                  id="totalDays"
                  v-model="addForm.totalDays"
                  type="number"
                  readonly
                  class="modern-input"
                  :placeholder="$t('Auto calculated')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form-section">
          <h6 class="section-title">
            <feather-icon icon="FileTextIcon" size="16" class="mr-2" />
            {{ $t("Additional Information") }}
          </h6>
          <b-row class="form-row-modern">
            <b-col md="12">
              <b-form-group
                :label="$t('Note')"
                label-for="note"
                :invalid-feedback="addFormErrors.note"
                class="modern-form-group"
              >
                <b-form-textarea
                  id="note"
                  v-model="addForm.note"
                  rows="4"
                  class="modern-textarea"
                  :placeholder="$t('Enter leave request note (optional)')"
                  :class="{ 'is-invalid': addFormErrors.note }"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row-modern">
            <b-col md="12">
              <b-form-group
                :label="$t('Attachment')"
                label-for="attachment"
                class="modern-form-group"
              >
                <div class="file-upload-modern">
                  <div class="leave-file-picker">
                    <div class="leave-file-picker__field">
                      <span
                        class="leave-file-picker__name"
                        :class="{ 'text-muted': !addFormAttachmentDisplayName }"
                        :title="addFormAttachmentDisplayName || $t('Choose file or drop it here') + '...'"
                      >
                        {{
                          addFormAttachmentDisplayName ||
                            $t("Choose file or drop it here") + "..."
                        }}
                      </span>
                      <b-button
                        variant="primary"
                        size="sm"
                        class="leave-file-picker__btn"
                        :disabled="addLoading"
                        @click="$refs.addFormAttachmentInput.click()"
                      >
                        {{ $t("Browse") }}
                      </b-button>
                    </div>
                    <input
                      ref="addFormAttachmentInput"
                      id="attachment"
                      type="file"
                      class="d-none"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      @change="handleAddFormAttachmentChange"
                    />
                  </div>
                </div>
                <small class="form-text-modern">
                  <feather-icon icon="InfoIcon" size="12" class="mr-1" />
                  {{ $t("Supported formats") }}: PDF, DOC, DOCX, JPG, JPEG, PNG
                </small>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-form>

      <template #modal-footer>
        <div class="modal-footer-modern">
          <b-button
            variant="outline-secondary"
            @click="showAddModal = false"
            class="btn-modern-cancel"
          >
            {{ $t("Cancel") }}
          </b-button>
          <b-button
            variant="primary"
            @click="submitAddLeave"
            :disabled="!isAddFormValid || addLoading"
            class="btn-modern-primary"
          >
            <b-spinner v-if="addLoading" small type="grow" class="mr-2" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-2" />
            {{ $t("Save Leave Request") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Note Modal -->
    <b-modal
      v-model="showNoteModal"
      :title="$t(selectedNoteTitle)"
      size="lg"
      @hidden="
        selectedNote = '';
        selectedNoteTitle = '';
        selectedNoteItem = null;
      "
    >
      <div class="note-modal-content">
        <!-- Date Information Header -->
        <div v-if="selectedNoteItem" class="note-header-info">
          <!-- Leave Note - Show Leave Dates -->
          <div
            v-if="selectedNoteTitle === 'Leave Note'"
            class="info-single-line"
          >
            <feather-icon
              icon="CalendarIcon"
              size="16"
              class="mr-2 text-primary"
            />
            <span class="info-text">
              <strong>{{ $t("Leave Period") }}:</strong>
              {{ formatDate(selectedNoteItem.dtFrom) }} -
              {{ formatDate(selectedNoteItem.dtTo) }} |
              <strong>{{ $t("Total Days") }}:</strong>
              {{ selectedNoteItem.totalDays }} {{ $t("day(s)") }}
            </span>
          </div>

          <!-- Admin Note - Show Approval Information -->
          <div
            v-else-if="selectedNoteTitle === 'Admin Note'"
            class="info-single-line"
          >
            <!-- Show approval dates only if not pending -->
            <template v-if="selectedNoteItem.status !== 'Pending'">
              <feather-icon
                icon="CalendarIcon"
                size="16"
                class="mr-2 text-primary"
              />
              <span class="info-text">
                <strong>{{ $t("Approved Period") }}:</strong>
                {{ formatDate(selectedNoteItem.dtApprovedFrom) }} -
                {{ formatDate(selectedNoteItem.dtApprovedTo) }}
                <template
                  v-if="
                    selectedNoteItem.status === 'Approved' &&
                    selectedNoteItem.meta_approvedBy
                  "
                >
                  | <strong>{{ $t("Approved By") }}:</strong>
                  {{ selectedNoteItem.meta_approvedBy }}
                </template>
                <template
                  v-if="
                    selectedNoteItem.status === 'Rejected' &&
                    selectedNoteItem.meta_rejectedBy
                  "
                >
                  | <strong>{{ $t("Rejected By") }}:</strong>
                  {{ selectedNoteItem.meta_rejectedBy }}
                </template>
              </span>
            </template>
            <template v-else>
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="mr-2 text-warning"
              />
              <span class="info-text">
                <strong>{{ $t("Status") }}:</strong>
                {{ $t("Pending Approval") }}
              </span>
            </template>
          </div>
        </div>

        <!-- Note Content -->
        <div class="note-text-full">
          {{ selectedNote }}
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showNoteModal = false">
          {{ $t("Close") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Attachment Modal -->
    <b-modal
      v-model="showAttachmentModal"
      :title="$t('Attachment Preview')"
      size="xl"
      @hidden="closeAttachmentModal"
      @keydown.left="prevImage"
      @keydown.right="nextImage"
    >
      <div class="attachment-modal-content">
        <div v-if="selectedAttachments.length > 0" class="attachment-gallery">
          <!-- Current Image Container -->
          <div class="current-image-container">
            <!-- Loading Spinner -->
            <div v-if="imageLoading" class="image-loading">
              <b-spinner variant="primary" style="width: 2rem; height: 2rem" />
              <p class="mt-2 text-muted">{{ $t("Loading") }}...</p>
            </div>

            <!-- Image Display -->
            <img
              v-if="
                isImageFile(selectedAttachments[currentImageIndex]) &&
                !imageLoading
              "
              :src="selectedAttachments[currentImageIndex]"
              :alt="$t('Attachment') + ' ' + (currentImageIndex + 1)"
              class="attachment-image"
              @load="imageLoading = false"
              @error="imageLoading = false"
            />

            <!-- File Preview -->
            <div
              v-else-if="!isImageFile(selectedAttachments[currentImageIndex])"
              class="file-preview"
            >
              <feather-icon icon="FileIcon" size="48" class="text-muted" />
              <p class="mt-2 text-muted">
                {{ getFileName(selectedAttachments[currentImageIndex]) }}
              </p>
              <a
                :href="selectedAttachments[currentImageIndex]"
                target="_blank"
                class="btn btn-primary btn-sm"
              >
                <feather-icon icon="ExternalLinkIcon" size="16" class="mr-1" />
                {{ $t("Open File") }}
              </a>
            </div>
          </div>
        </div>

        <div v-else class="text-center text-muted">
          <p>{{ $t("No attachments available") }}</p>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showAttachmentModal = false"
        >
          {{ $t("Close") }}
        </b-button>
        <a
          v-if="selectedAttachments[currentImageIndex]"
          :href="selectedAttachments[currentImageIndex]"
          target="_blank"
          class="btn btn-primary"
        >
          <feather-icon icon="ExternalLinkIcon" class="mr-1" />
          {{ $t("Open in New Tab") }}
        </a>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BModal,
  BFormGroup,
  BSpinner,
  BBadge,
  BPagination,
  BAvatar,
  VBTooltip,
  BForm,
  BFormInput,
  BFormTextarea,
  BFormFile,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";

export default {
  name: "LeaveRequest",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BModal,
    BFormGroup,
    BSpinner,
    BBadge,
    BPagination,
    BAvatar,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormFile,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      leaveRequests: [],
      showModal: false,
      selectedLeaveRequest: null,
      dataLoading: true,
      leaveListReady: false,
      viewLoading: null,
      showImageModal: false,
      selectedImageUrl: null,
      showApproveModal: false,
      showRejectModal: false,
      showNoteModal: false,
      selectedNote: "",
      selectedNoteTitle: "",
      selectedNoteItem: null,
      showAttachmentModal: false,
      selectedAttachments: [],
      currentImageIndex: 0,
      imageLoading: false,
      imagesPreloaded: false,
      selectedActionItem: null,
      approveLoading: false,
      rejectLoading: false,
      approveForm: {
        dtApprovedFrom: null,
        dtApprovedTo: null,
        exNote: "",
      },
      rejectForm: {
        exNote: "",
      },
      // Add modal data
      showAddModal: false,
      addLoading: false,
      addForm: {
        id: 0,
        tID: null,
        departmentID: null,
        leaveType: null,
        leaveTypeOption: null,
        dtFrom: null,
        dtTo: null,
        dateRange: null,
        totalDays: 0,
        note: "",
        attachment: "",
        campusID:
          (this.$store.state.userData && this.$store.state.userData.cId) || 0,
      },
      addFormErrors: {
        staffID: "",
        leaveType: "",
        dtFrom: "",
        dtTo: "",
        dateRange: "",
        note: "",
      },
      // Add modal specific dropdowns
      addModalDepartmentOptions: [],
      addModalStaffOptions: [],
      addModalDepartmentLoading: false,
      addModalStaffLoading: false,
      addFormAttachmentFile: null,
      leaveTypeOptions: [],
      leaveTypesLoading: false,
      leaveTypeSelectKey: 0,
      dynamicLeaveBalanceSetting: null,
      campusesLoading: false,
      staffLoading: false,
      // Head office data
      headOfficeObj: null,
      headOfficeCampusId: null,
      filters: {
        search: "",
        dateRange: null,
        dateFrom: null,
        dateTo: null,
        campusID: null,
        sortBy: "date",
        sortOrder: "desc",
      },
      campusOptions: [],
      staffOptions: [],
      departmentOptions: [],
      statusOptions: [
        { value: null, text: "All Status" },
        { value: "Pending", text: "Pending" },
        { value: "Approved", text: "Approved" },
        { value: "Rejected", text: "Rejected" },
      ],
      // Pagination
      currentPage: 1,
      pageSize: 20,
      totalRecords: 0,
      totalPages: 0,
      showPagination: true,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "40", value: 40 },
        { label: "80", value: 80 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
      ],
      // Date picker config
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      // Date range picker config
      dateRangeConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        placeholder: "Select date range",
      },
    };
  },
  computed: {
    /** Head office users see leave requests from every campus. */
    showCampusColumn() {
      return this.isHeadOfficeUser || this.shouldShowCampusDropdown;
    },
    fields() {
      const columns = [
        { key: "sno", label: "S.No", sortable: false },
        { key: "tID", label: "Employee ID", sortable: true },
        { key: "name", label: "Name", sortable: true },
      ];
      if (this.showCampusColumn) {
        columns.push({ key: "campus", label: "Campus", sortable: false });
      }
      columns.push(
        { key: "leaveType", label: "Leave Type", sortable: true },
        { key: "dtFrom", label: "From Date", sortable: true },
        { key: "dtTo", label: "To Date", sortable: true },
        { key: "totalDays", label: "Days", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "leaveNote", label: "Leave Note", sortable: false },
        { key: "adminNote", label: "Admin Note", sortable: false },
        { key: "attachment", label: "Attachments", sortable: false },
        { key: "actions", label: "Actions" }
      );
      return columns;
    },
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      if (this.tableBusy || !this.leaveListReady) {
        return "";
      }
      return this.$t("No leave requests found");
    },

    addFormAttachmentDisplayName() {
      return this.addFormAttachmentFile && this.addFormAttachmentFile.name
        ? this.addFormAttachmentFile.name
        : "";
    },

    hasActiveFilters() {
      // Check if any filters are applied
      return (
        (this.filters.search && this.filters.search.trim() !== "") ||
        this.filters.dateRange !== null ||
        (this.filters.dateFrom !== null && this.filters.dateTo !== null)
      );
    },

    displayedRange() {
      if (this.totalRecords === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
      return { start, end };
    },

    isApproveFormValid() {
      const hasFrom = !!this.approveForm.dtApprovedFrom;
      const hasNote =
        this.approveForm.exNote && this.approveForm.exNote.trim().length > 0;
      return hasFrom && hasNote && this.isDateRangeValid();
    },

    isRejectFormValid() {
      return this.rejectForm.exNote && this.rejectForm.exNote.trim().length > 0;
    },

    approvedFromDateConfig() {
      if (!this.selectedActionItem) return this.dateConfig;

      const originalFrom = this.formatDateValue(this.selectedActionItem.dtFrom);
      const originalTo = this.formatDateValue(
        this.selectedActionItem.dtTo || this.selectedActionItem.dtFrom
      );
      if (!originalFrom || !originalTo) return this.dateConfig;

      return {
        ...this.dateConfig,
        minDate: originalFrom,
        maxDate: originalTo,
        defaultDate: originalFrom,
      };
    },

    approvedToDateConfig() {
      if (!this.selectedActionItem) return this.dateConfig;

      const originalFrom = this.formatDateValue(this.selectedActionItem.dtFrom);
      const originalTo = this.formatDateValue(
        this.selectedActionItem.dtTo || this.selectedActionItem.dtFrom
      );
      const approvedFrom =
        this.formatDateValue(this.approveForm.dtApprovedFrom) || originalFrom;
      if (!originalFrom || !originalTo) return this.dateConfig;

      return {
        ...this.dateConfig,
        minDate: approvedFrom,
        maxDate: originalTo,
        defaultDate: this.formatDateValue(this.approveForm.dtApprovedTo) || originalTo,
      };
    },

    // Add modal computed properties
    isDynamicLeaveBalance() {
      const s = this.dynamicLeaveBalanceSetting;
      if (!s) return false;
      if (s.valueBool === true) return true;
      const v = s.value;
      return (
        v === true ||
        v === 1 ||
        String(v).toLowerCase() === "true" ||
        String(v) === "1"
      );
    },
    selectedLeaveTypeMeta() {
      if (this.addForm.leaveTypeOption) return this.addForm.leaveTypeOption;
      if (!this.addForm.leaveType) return null;
      return (
        (this.leaveTypeOptions || []).find(
          (o) => o.value === this.addForm.leaveType
        ) || null
      );
    },
    isAddFormValid() {
      return (
        this.addForm.tID &&
        this.addForm.leaveType &&
        this.addForm.dtFrom &&
        this.addForm.dtTo &&
        this.addForm.totalDays > 0
      );
    },

    toDateConfig() {
      if (!this.addForm.dtFrom) return this.dateConfig;

      const fromDate = new Date(this.addForm.dtFrom);

      return {
        ...this.dateConfig,
        minDate: fromDate,
        defaultDate: fromDate,
      };
    },

    shouldShowCampusDropdown() {
      // Show campus dropdown only if user's cId is 1
      if (this.$store.state.userData && this.$store.state.userData.cId) {
        return this.$store.state.userData.cId === 1;
      }
      return false;
    },

    isHeadOfficeUser() {
      // Check if current user is head office user
      if (
        this.headOfficeCampusId &&
        this.$store.state.userData &&
        this.$store.state.userData.cId
      ) {
        const isHeadOffice =
          this.$store.state.userData.cId === this.headOfficeCampusId;
        console.log("Is Head Office User:", isHeadOffice);
        return isHeadOffice;
      }
      return false;
    },
  },
  async created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    if (this.$store.state.menu) {
      this.$store.state.menu.forEach((el) => {
        if (el.children) {
          el.children.forEach((ch) => {
            right[ch.route] = {
              view: ch.view,
              add: ch.add,
              edit: ch.edit,
              delete: ch.delete,
            };
          });
        }
      });
    }
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.dataLoading = false;
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.dataLoading = false;
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.dataLoading = true;
    this.leaveListReady = false;

    // Hit Leave Types API early (without tID; reloads with tID when dynamic + staff selected)
    await this.loadCampusLeaveSettings();
    this.loadLeaveTypes();

    await this.loadLeaveRequests();

    // Load settings and initial data
    await this.loadSettings();
    await this.loadInitialData();
  },

  mounted() {
    // Sync top scrollbar width with table width
    this.$nextTick(() => {
      this.updateScrollbarWidth();
    });

    // Add click-outside handler to close v-select dropdowns
    this.handleClickOutside = (event) => {
      // Check if click is on any v-select component or its dropdown menu
      const clickedElement = event.target;
      const isVSelectClick =
        clickedElement.closest(".v-select") ||
        clickedElement.closest(".vs__dropdown-menu") ||
        clickedElement.closest(".vs__dropdown-option") ||
        clickedElement.closest(".vs__dropdown-toggle") ||
        clickedElement.closest(".vs__actions") ||
        clickedElement.closest(".vs__selected-options");

      // If clicked outside, close all open dropdowns
      if (!isVSelectClick) {
        // Use Vue component refs if available to close dropdowns properly
        const vSelectRefs = ["pageSizeSelect"];
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName] && this.$refs[refName].$el) {
            const toggle = this.$refs[refName].$el.querySelector(
              ".vs__dropdown-toggle"
            );
            if (toggle) {
              toggle.blur();
            }
          }
        });

        // Blur all focused v-select toggles
        const allToggles = document.querySelectorAll(".vs__dropdown-toggle");
        allToggles.forEach((toggle) => {
          if (document.activeElement === toggle) {
            toggle.blur();
          }
        });

        // Close all visible dropdown menus by finding their parent v-select components
        const dropdownMenus = document.querySelectorAll(".vs__dropdown-menu");
        dropdownMenus.forEach((menu) => {
          const style = window.getComputedStyle(menu);
          if (style.display !== "none" && style.visibility !== "hidden") {
            // Since menu is appended to body, we need to find v-select by checking all v-selects
            const allVSelects = document.querySelectorAll(".v-select");
            allVSelects.forEach((vSelect) => {
              const toggle = vSelect.querySelector(".vs__dropdown-toggle");
              if (toggle && document.activeElement === toggle) {
                toggle.blur();
              }
            });
          }
        });

        // Force blur on document active element if it's a v-select toggle
        if (
          document.activeElement &&
          document.activeElement.classList.contains("vs__dropdown-toggle")
        ) {
          document.activeElement.blur();
        }
      }
    };

    // Add event listeners for both click and mousedown
    document.addEventListener("click", this.handleClickOutside, true);
    document.addEventListener("mousedown", this.handleClickOutside, true);
  },

  beforeDestroy() {
    // Remove event listeners
    if (this.handleClickOutside) {
      document.removeEventListener("click", this.handleClickOutside, true);
      document.removeEventListener("mousedown", this.handleClickOutside, true);
    }
  },

  watch: {
    "approveForm.dtApprovedFrom"() {
      // Trigger reactivity for approved to date config
      this.$forceUpdate();
    },
    "addForm.campusID"() {
      // Reload staff when campus changes in add modal
      if (this.showAddModal) {
        this.loadStaffForAddModal();
      }
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadCampusLeaveSettings() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: ["dynamic_leave_balance"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        const list = Array.isArray(res) ? res : [];
        this.dynamicLeaveBalanceSetting =
          list.find((el) => el.key === "dynamic_leave_balance") || null;
      } catch (error) {
        console.error("Error loading leave settings:", error);
        this.dynamicLeaveBalanceSetting = null;
      }
    },

    async loadSettings() {
      try {
        // Load campuses to find head office
        const obj = {
          url: `${this.$store.state.domain}campuses?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        };

        const campuses = await this.get(obj);

        if (campuses && Array.isArray(campuses)) {
          // Find head office campus by branch name
          this.headOfficeObj = campuses.find(
            (campus) =>
              campus.branch &&
              campus.branch.toLowerCase().includes("head office")
          );

          if (this.headOfficeObj) {
            this.headOfficeCampusId = this.headOfficeObj.id;
            console.log("Head Office Campus:", this.headOfficeObj);
            console.log("Head Office Campus ID:", this.headOfficeCampusId);
            console.log("User Campus ID:", this.$store.state.userData?.cId);
            console.log("Is Head Office User:", this.isHeadOfficeUser);
          }
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    },

    extractLeaveTypesList(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (res.data && Array.isArray(res.data.data)) return res.data.data;
      if (
        res.status === "success" &&
        res.data &&
        Array.isArray(res.data)
      ) {
        return res.data;
      }
      return [];
    },

    leaveTypeNum(item, ...keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (item[k] != null && item[k] !== "") {
          const n = Number(item[k]);
          if (!Number.isNaN(n)) return n;
        }
      }
      return null;
    },

    mapLeaveTypeOption(item) {
      const name = item.name || item.leaveType || item.title || "";
      const allowed = this.leaveTypeNum(
        item,
        "allowed",
        "Allowed",
        "count",
        "Count",
        "total",
        "Total"
      );
      const utilized = this.leaveTypeNum(
        item,
        "utilized",
        "Utilized",
        "used",
        "Used"
      );
      let remaining = this.leaveTypeNum(
        item,
        "remaining",
        "Remaining",
        "balance",
        "Balance"
      );
      if (remaining == null && allowed != null) {
        remaining = allowed;
      }
      const canApply =
        item.canApply != null
          ? !!item.canApply
          : remaining == null
          ? true
          : remaining > 0;

      return {
        value: name,
        text: name,
        allowed,
        utilized,
        remaining,
        canApply,
        dynamicLeaveBalance: !!item.dynamicLeaveBalance,
        raw: item,
      };
    },

    resolveLeaveTypeOption(option) {
      if (option && typeof option === "object" && (option.value || option.text)) {
        return option;
      }
      if (this.addForm.leaveTypeOption) return this.addForm.leaveTypeOption;
      const value =
        typeof option === "string"
          ? option
          : this.addForm.leaveType || "";
      if (!value) return null;
      return (
        (this.leaveTypeOptions || []).find((o) => o.value === value) || {
          value,
          text: value,
        }
      );
    },

    leaveTypeDisplayName(option) {
      const o = this.resolveLeaveTypeOption(option);
      return o ? o.text || o.value : "";
    },

    hasLeaveTypeBalance(option) {
      const o = this.resolveLeaveTypeOption(option);
      if (!o || !this.isDynamicLeaveBalance) return false;
      return (
        (o.allowed != null && !Number.isNaN(Number(o.allowed))) ||
        (o.remaining != null && !Number.isNaN(Number(o.remaining)))
      );
    },

    formatLeaveCount(val) {
      if (val == null || val === "" || Number.isNaN(Number(val))) return "—";
      return Number(val);
    },

    leaveTypeRatioLabel(option) {
      if (!this.isDynamicLeaveBalance) return null;
      const o = this.resolveLeaveTypeOption(option);
      if (!o || !this.hasLeaveTypeBalance(o)) return null;
      const total = this.formatLeaveCount(o.allowed);
      const rem = this.formatLeaveCount(o.remaining);
      if (total === "—" && rem === "—") return null;
      return rem + "/" + total;
    },

    leaveTypeRatioClass(option) {
      const o = this.resolveLeaveTypeOption(option);
      if (!o || o.remaining == null) return "lt-selected-ratio--ok";
      return Number(o.remaining) > 0
        ? "lt-selected-ratio--ok"
        : "lt-selected-ratio--zero";
    },

    onLeaveTypeOptionSelect(option) {
      this.addForm.leaveType = option ? option.value : null;
      this.addFormErrors.leaveType = "";
    },

    selectLeaveTypeOption(opt) {
      if (!opt) return;
      this.addForm.leaveTypeOption = opt;
      this.addForm.leaveType = opt.value;
      this.addFormErrors.leaveType = "";
    },

    syncLeaveTypeOptionFromValue() {
      if (!this.addForm.leaveType) {
        this.addForm.leaveTypeOption = null;
        return;
      }
      const found = (this.leaveTypeOptions || []).find(
        (o) => o.value === this.addForm.leaveType
      );
      this.addForm.leaveTypeOption = found || null;
    },

    async loadLeaveTypes(tID) {
      this.leaveTypesLoading = true;
      try {
        const campusID =
          this.addForm.campusID || this.$store.state.userData.cId;
        let url =
          this.$store.state.domain +
          "LeaveType/GetLeaveTypes?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          campusID;

        const staffId =
          tID !== undefined && tID !== null && tID !== ""
            ? tID
            : this.addForm.tID;
        if (this.isDynamicLeaveBalance && staffId) {
          url += "&tID=" + staffId;
        }

        const obj = {
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        const list = this.extractLeaveTypesList(res);
        this.leaveTypeOptions = list.map((item) =>
          this.mapLeaveTypeOption(item)
        );
        this.leaveTypeSelectKey += 1;
        this.syncLeaveTypeOptionFromValue();
      } catch (error) {
        console.error("Error loading leave types:", error);
        this.leaveTypeOptions = [];
        this.leaveTypeSelectKey += 1;
      } finally {
        this.leaveTypesLoading = false;
      }
    },

    async loadInitialData() {
      await this.loadCampuses();
      // Load departments for the initial campus selection
      // For non-head office users, use their campus ID directly
      const campusID = this.isHeadOfficeUser
        ? this.filters.campusID
        : this.$store.state.userData.cId;
      await this.loadDepartmentsForCampus(campusID);
    },

    async loadCampuses() {
      this.campusesLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const campuses = await this.get(obj);

        // Show all campuses without "All" option
        this.campusOptions = campuses;

        // Set default campus ID after loading
        if (this.isHeadOfficeUser) {
          // For head office users, select the first campus
          this.filters.campusID =
            campuses.length > 0
              ? campuses[0].id
              : this.$store.state.userData.cId;
        } else {
          this.filters.campusID = this.$store.state.userData.cId; // User's campus for others
        }
      } catch (error) {
        console.error("Error loading campuses:", error);
        this.$bvToast.toast(this.$t("Error loading campuses"), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.campusesLoading = false;
      }
    },

    async loadDepartments() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const departments = await this.get(obj);
        this.departmentOptions = departments || [];
      } catch (error) {
        console.error("Error loading departments:", error);
        this.$bvToast.toast(this.$t("Error loading departments"), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadDepartmentsForCampus(campusID) {
      try {
        let url =
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db;

        if (campusID) {
          url += "&cId=" + campusID;
        } else {
          // Fallback to user's campus if no campus ID provided
          url += "&cId=" + this.$store.state.userData.cId;
        }

        console.log("Loading departments with URL:", url);

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };
        const departments = await this.get(obj);
        this.departmentOptions = departments || [];
        console.log(
          "Departments loaded:",
          this.departmentOptions.length,
          "departments"
        );
      } catch (error) {
        console.error("Error loading departments for campus:", error);
        this.departmentOptions = [];
      }
    },

    async loadStaffForDepartment(departmentID) {
      this.staffLoading = true;
      try {
        // Use the pagination API for staff loading
        let url = this.$store.state.domain + "staff/LoadDataPagination?";

        // Required parameters
        url += "db=" + this.$store.state.userData.db;

        // Use the campus ID from filters
        const targetCampusID = this.filters.campusID;

        if (targetCampusID) {
          url += "&cId=" + targetCampusID;
        } else {
          // Fallback to user's campus if no campus ID
          url += "&cId=" + this.$store.state.userData.cId;
        }

        // Add department filter if selected
        if (departmentID) {
          url += "&dptID=" + departmentID;
        }

        // Add other required parameters
        url += "&status=present"; // Only show present staff
        url += "&page=1"; // First page
        url += "&appointedFrom="; // Empty for no filter
        url += "&appointedTo="; // Empty for no filter
        url += "&designation="; // Empty for no filter
        url += "&sortBy=name"; // Sort by name
        url += "&sortOrder=asc"; // Ascending order

        console.log("Loading staff with URL:", url);

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Handle paginated response
        if (response && response.data && response.data.data) {
          this.staffOptions = response.data.data.filter(
            (staff) => staff.isActive !== false
          );
          console.log(
            "Staff loaded:",
            this.staffOptions.length,
            "staff members"
          );
        } else if (response && Array.isArray(response)) {
          // Fallback for non-paginated response
          this.staffOptions = response.filter(
            (staff) => staff.isActive !== false
          );
          console.log(
            "Staff loaded (fallback):",
            this.staffOptions.length,
            "staff members"
          );
        } else {
          this.staffOptions = [];
          console.log("No staff data received");
        }
      } catch (error) {
        console.error("Error loading staff for department:", error);
        this.staffOptions = [];
      } finally {
        this.staffLoading = false;
      }
    },

    async loadStaff(campusID = null) {
      this.staffLoading = true;
      try {
        // Use the pagination API for staff loading
        let url = this.$store.state.domain + "staff/LoadDataPagination?";

        // Required parameters
        url += "db=" + this.$store.state.userData.db;

        // Use the campus ID from filters or parameter
        const targetCampusID = campusID || this.filters.campusID;

        if (targetCampusID) {
          url += "&cId=" + targetCampusID;
        } else {
          // Fallback to user's campus if no campus ID
          url += "&cId=" + this.$store.state.userData.cId;
        }

        // Add other required parameters
        url += "&status=present"; // Only show present staff
        url += "&page=1"; // First page
        url += "&appointedFrom="; // Empty for no filter
        url += "&appointedTo="; // Empty for no filter
        url += "&designation="; // Empty for no filter
        url += "&sortBy=name"; // Sort by name
        url += "&sortOrder=asc"; // Ascending order

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Handle paginated response
        if (response && response.data && response.data.data) {
          this.staffOptions = response.data.data.filter(
            (staff) => staff.isActive !== false
          );
        } else if (response && Array.isArray(response)) {
          // Fallback for non-paginated response
          this.staffOptions = response.filter(
            (staff) => staff.isActive !== false
          );
        } else {
          this.staffOptions = [];
        }
      } catch (error) {
        console.error("Error loading staff:", error);
        this.$bvToast.toast("Error loading staff data!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.staffLoading = false;
      }
    },

    async loadLeaveRequests() {
      this.dataLoading = true;
      try {
        // Build URL for pagination API
        let url = this.$store.state.domain + "LeaveRequests/GetByPagination?";

        // Required parameters
        url += "db=" + this.$store.state.userData.db;

        // Use user's campus ID
        const campusID = this.$store.state.userData.cId;
        url += "&cID=" + campusID;

        // Add userID parameter
        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.localID ||
          1;
        url += "&userID=" + userID;

        // Add loadAll parameter: 
        // For myskooliuss: true only if campusID is 1
        // For others: true by default
        const reportDomain = this.$store.state.report.replace(/_/g, "");
        const loadAll = reportDomain.includes("myskooliuss")
          ? (campusID === 1 ? "true" : "false")
          : "true";
        url += "&loadAll=" + loadAll;

        // Add dt parameter (today's date in YYYY-MM-DD format)
        const today = new Date();
        const dt = today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
        url += "&dt=" + dt;

        // Pagination parameters
        url += "&pageNumber=" + this.currentPage;
        url += "&pageSize=" + this.pageSize;

        // Filter parameters
        if (this.filters.search && this.filters.search.trim() !== "") {
          url += "&search=" + encodeURIComponent(this.filters.search.trim());
        }
        if (this.filters.dateFrom) {
          url += "&dateFrom=" + new Date(this.filters.dateFrom).toISOString();
        }
        if (this.filters.dateTo) {
          url += "&dateTo=" + new Date(this.filters.dateTo).toISOString();
        }

        // Sort parameters
        url += "&sortBy=" + this.filters.sortBy;
        url += "&sortOrder=" + this.filters.sortOrder;

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };

        let res = await this.get(obj);

        // Handle paginated response
        if (res && res.data && res.data.data) {
          let allRecords = res.data.data || [];

          // Client-side limiting if API returns more records than pageSize
          // This is a fallback in case API doesn't respect pageSize parameter
          if (allRecords.length > this.pageSize) {
            console.warn(
              `API returned ${allRecords.length} records but pageSize is ${this.pageSize}. Limiting client-side.`
            );
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            this.leaveRequests = allRecords.slice(startIndex, endIndex);
          } else {
            this.leaveRequests = allRecords;
          }

          this.totalRecords = res.data.totalRecords || allRecords.length;
          this.totalPages =
            res.data.totalPages || Math.ceil(this.totalRecords / this.pageSize);

          console.log("API Response:", {
            requestedPageSize: this.pageSize,
            receivedRecords: allRecords.length,
            displayedRecords: this.leaveRequests.length,
            totalRecords: this.totalRecords,
            currentPage: this.currentPage,
          });
        } else {
          this.leaveRequests = [];
          this.totalRecords = 0;
          this.totalPages = 0;
          console.log("No leave requests data received");
        }
      } catch (error) {
        console.error("Error loading leave requests:", error);
        this.$bvToast.toast("Error loading leave requests!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.leaveRequests = [];
        this.totalRecords = 0;
        this.totalPages = 0;
      } finally {
        this.dataLoading = false;
        this.leaveListReady = true;
      }

      // Update scrollbar width after data loads
      this.$nextTick(() => {
        this.updateScrollbarWidth();
      });
    },

    onSearchChange() {
      this.currentPage = 1; // Reset to first page when search changes
      this.loadLeaveRequests();
    },

    onDateRangeChange(selectedDates, dateStr, instance) {
      if (selectedDates && selectedDates.length === 2) {
        this.filters.dateFrom = selectedDates[0];
        this.filters.dateTo = selectedDates[1];
      } else {
        this.filters.dateFrom = null;
        this.filters.dateTo = null;
      }
      this.currentPage = 1; // Reset to first page
      this.loadLeaveRequests();
    },

    clearFilters() {
      // Clear all filters
      this.filters.search = "";
      this.filters.dateRange = null;
      this.filters.dateFrom = null;
      this.filters.dateTo = null;

      // Reset pagination and reload data
      this.currentPage = 1;
      this.loadLeaveRequests();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadLeaveRequests();
    },

    onPageSizeChange() {
      this.currentPage = 1; // Reset to first page when page size changes
      this.loadLeaveRequests();
    },

    closePageSizeDropdown() {
      // Close the dropdown after option is selected
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          // Close the dropdown by removing focus
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) {
            toggle.blur();
            // Also trigger a click on document to simulate outside click
            setTimeout(() => {
              document.body.click();
            }, 100);
          }
        }
      });
    },

    syncScroll() {
      // Sync top scrollbar with main table scroll
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar.querySelector(
          ".table-scrollbar-content"
        );

        if (scrollContent) {
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      }
    },

    updateScrollbarWidth() {
      // Update top scrollbar content width to match table scroll width
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (
            scrollContent &&
            tableWrapper.scrollWidth > tableWrapper.clientWidth
          ) {
            scrollContent.style.width = tableWrapper.scrollWidth + "px";

            // Sync scroll on top scrollbar drag
            topScrollbar.addEventListener("scroll", () => {
              tableWrapper.scrollLeft = topScrollbar.scrollLeft;
            });
          }
        }
      });
    },

    onSortChanged(ctx) {
      // Map table sort field names to API field names
      const fieldMapping = {
        tID: "tID",
        name: "name",
        designation: "designation",
        department: "department",
        leaveType: "leaveType",
        dtFrom: "dtFrom",
        dtTo: "dtTo",
        totalDays: "totalDays",
        status: "status",
        date: "date",
      };

      this.filters.sortBy = fieldMapping[ctx.sortBy] || ctx.sortBy;
      this.filters.sortOrder = ctx.sortDesc ? "desc" : "asc";
      this.currentPage = 1; // Reset to first page when sorting changes
      this.loadLeaveRequests();
    },

    async viewLeaveRequest(item) {
      this.viewLoading = item.id;
      try {
        // Get detailed leave request data using GetById API
        const obj = {
          url: `${this.$store.state.domain}LeaveRequests/GetById/${item.id}?db=${this.$store.state.userData.db}&cID=${item.campusID}`,
          token: this.$store.state.userData.token,
        };

        console.log("View Leave Request URL:", obj.url);
        console.log("View Leave Request Item:", item);

        const response = await this.get(obj);

        if (response && response.data && response.status === "success") {
          this.selectedLeaveRequest = response.data;
          console.log("View API Response Data:", response.data);
        } else {
          // Fallback to the item data if API fails
          this.selectedLeaveRequest = item;
          console.log("Using fallback data:", item);
        }

        this.showModal = true;
      } catch (error) {
        console.error("Error loading leave request details:", error);
        // Fallback to the item data if API fails
        this.selectedLeaveRequest = item;
        this.showModal = true;
      } finally {
        this.viewLoading = null;
      }
    },

    openApproveModal(item) {
      this.selectedActionItem = item;
      this.syncApproveFormDatesFromItem(item);
      this.approveForm.exNote = "";
      this.showApproveModal = true;
    },

    syncApproveFormDatesFromItem(item) {
      if (!item) return;
      const from = this.formatDateValue(item.dtFrom);
      const to = this.formatDateValue(item.dtTo || item.dtFrom);
      this.approveForm.dtApprovedFrom = from;
      this.approveForm.dtApprovedTo = to || from;
    },

    onApproveModalShown() {
      this.$nextTick(() => {
        if (this.selectedActionItem) {
          this.syncApproveFormDatesFromItem(this.selectedActionItem);
        }
      });
    },

    onApprovedFromDateChange(selectedDates) {
      if (!selectedDates || !selectedDates.length) return;

      const formatted = this.formatDateValue(selectedDates[0]);
      if (!formatted) return;

      this.approveForm.dtApprovedFrom = formatted;

      if (
        !this.approveForm.dtApprovedTo ||
        this.approveForm.dtApprovedTo < formatted
      ) {
        this.approveForm.dtApprovedTo = formatted;
      }
    },

    openRejectModal(item) {
      this.selectedActionItem = item;
      this.rejectForm = {
        exNote: "",
      };
      this.showRejectModal = true;
    },

    resetApproveForm() {
      this.approveForm = {
        dtApprovedFrom: null,
        dtApprovedTo: null,
        exNote: "",
      };
      this.selectedActionItem = null;
    },

    resetRejectForm() {
      this.rejectForm = {
        exNote: "",
      };
      this.selectedActionItem = null;
    },

    isDateRangeValid() {
      if (!this.approveForm.dtApprovedFrom) {
        return false;
      }

      const approvedFrom = new Date(this.approveForm.dtApprovedFrom);
      const approvedTo = new Date(
        this.approveForm.dtApprovedTo || this.approveForm.dtApprovedFrom
      );

      if (
        Number.isNaN(approvedFrom.getTime()) ||
        Number.isNaN(approvedTo.getTime())
      ) {
        return false;
      }

      return approvedFrom <= approvedTo;
    },

    async submitApprove() {
      if (
        !this.approveForm.exNote ||
        this.approveForm.exNote.trim().length === 0
      ) {
        this.$bvToast.toast(
          this.$t("Please provide a reason for approving this leave request."),
          {
            title: this.$t("Validation Error"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      if (!this.approveForm.dtApprovedFrom) {
        this.$bvToast.toast(this.$t("Please select an approved from date."), {
          title: this.$t("Validation Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      if (!this.isDateRangeValid()) {
        this.$bvToast.toast(this.$t("Approved dates must be valid."), {
          title: this.$t("Validation Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.approveLoading = true;
      try {
        const approvedFromValue = this.toApiDate(
          this.approveForm.dtApprovedFrom
        );
        const approvedToValue = this.toApiDate(
          this.approveForm.dtApprovedTo || this.approveForm.dtApprovedFrom
        );
        const obj = {
          url: `${this.$store.state.domain}LeaveRequests/Approve/${this.selectedActionItem.campusID}/${this.selectedActionItem.id}?db=${this.$store.state.userData.db}`,
          token: this.$store.state.userData.token,
          body: {
            userID: this.$store.state.userData.userID,
            dtApprovedFrom: approvedFromValue,
            dtApprovedTo: approvedToValue,
            exNote: this.approveForm.exNote,
          },
        };

        // Use axios directly to avoid store toast issues
        const axios = require("axios");
        const response = await axios.post(obj.url, obj.body, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
            "Content-Type": "application/json",
          },
        });
        const responseData = response.data;

        console.log("Approve API Response:", responseData);
        console.log("Response type:", typeof responseData);
        console.log("Response status:", responseData?.status);
        console.log("Response message:", responseData?.message);
        console.log("Response data:", responseData?.data);

        // Check if the response indicates success - handle different response structures
        const isSuccess =
          responseData &&
          (responseData.status === "success" ||
            responseData.message ||
            (responseData.data && responseData.data.status === "Approved") ||
            (responseData.data &&
              responseData.data.data &&
              responseData.data.data.status === "Approved") ||
            (responseData.data && responseData.data.status) || // Any status in data indicates success
            responseData.data);

        console.log("Is Success:", isSuccess);

        if (isSuccess) {
          this.$bvToast.toast(
            responseData.message ||
              this.$t("Leave request has been approved successfully."),
            {
              title: this.$t("Approved!"),
              variant: "success",
              solid: true,
            }
          );
          this.showApproveModal = false;
          this.loadLeaveRequests();
        } else {
          console.log("Approve failed - Response:", responseData);
          this.$bvToast.toast(this.$t("Failed to approve leave request."), {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error approving leave request:", error);
        this.$bvToast.toast(
          this.$t("An error occurred while approving the request."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
        // Close modal even on error after showing the error message
        setTimeout(() => {
          this.showApproveModal = false;
        }, 2000);
      } finally {
        this.approveLoading = false;
      }
    },

    async submitReject() {
      if (
        !this.rejectForm.exNote ||
        this.rejectForm.exNote.trim().length === 0
      ) {
        this.$bvToast.toast(
          this.$t("Please provide a reason for rejecting this leave request."),
          {
            title: this.$t("Validation Error"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      this.rejectLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}LeaveRequests/Reject/${this.selectedActionItem.campusID}/${this.selectedActionItem.id}?db=${this.$store.state.userData.db}`,
          token: this.$store.state.userData.token,
          body: {
            userID: this.$store.state.userData.userID,
            dtApprovedFrom: null,
            dtApprovedTo: null,
            exNote: this.rejectForm.exNote,
          },
        };

        // Use axios directly to avoid store toast issues
        const axios = require("axios");
        const response = await axios.post(obj.url, obj.body, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
            "Content-Type": "application/json",
          },
        });
        const responseData = response.data;

        console.log("Reject API Response:", responseData);
        console.log("Response type:", typeof responseData);
        console.log("Response status:", responseData?.status);
        console.log("Response message:", responseData?.message);
        console.log("Response data:", responseData?.data);

        // Check if the response indicates success - handle different response structures
        const isSuccess =
          responseData &&
          (responseData.status === "success" ||
            responseData.message ||
            (responseData.data && responseData.data.status === "Rejected") ||
            (responseData.data &&
              responseData.data.data &&
              responseData.data.data.status === "Rejected") ||
            (responseData.data && responseData.data.status) || // Any status in data indicates success
            responseData.data);

        console.log("Is Success:", isSuccess);

        if (isSuccess) {
          this.$bvToast.toast(
            responseData.message ||
              this.$t("Leave request has been rejected successfully."),
            {
              title: this.$t("Rejected!"),
              variant: "success",
              solid: true,
            }
          );
          this.showRejectModal = false;
          this.loadLeaveRequests();
        } else {
          console.log("Reject failed - Response:", responseData);
          this.$bvToast.toast(this.$t("Failed to reject leave request."), {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error rejecting leave request:", error);
        this.$bvToast.toast(
          this.$t("An error occurred while rejecting the request."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
        // Close modal even on error after showing the error message
        setTimeout(() => {
          this.showRejectModal = false;
        }, 2000);
      } finally {
        this.rejectLoading = false;
      }
    },

    formatDateValue(value) {
      if (value == null || value === "") return null;
      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        const d = String(value.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      const s = String(value).trim();
      const isoDay = s.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoDay) return isoDay[1];
      const parsed = new Date(s);
      if (Number.isNaN(parsed.getTime())) return null;
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    toApiDate(value) {
      if (!value) return null;
      const parsed = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed.toISOString();
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    formatDateTime(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    campusLabel(item) {
      if (!item) return "—";
      if (item.campusName) return item.campusName;
      if (typeof item.campus === "string" && item.campus.trim()) {
        return item.campus.trim();
      }
      const campusID = item.campusID;
      if (!campusID) return "—";
      const campus = (this.campusOptions || []).find(
        (option) => Number(option.id) === Number(campusID)
      );
      return campus ? campus.branch || campus.name || "—" : "—";
    },

    getStatusVariant(status) {
      switch (status) {
        case "Approved":
          return "success";
        case "Pending":
          return "warning";
        case "Rejected":
          return "danger";
        default:
          return "secondary";
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case "Approved":
          return "badge-success";
        case "Pending":
          return "badge-warning";
        case "Rejected":
          return "badge-danger";
        default:
          return "badge-secondary";
      }
    },

    getApprovedRejectedBy(item) {
      if (item.status === "Pending") {
        return this.$t("Pending");
      } else if (item.status === "Approved" && item.meta_approvedBy) {
        return item.meta_approvedBy;
      } else if (item.status === "Rejected" && item.meta_rejectedBy) {
        return item.meta_rejectedBy;
      } else {
        return this.$t("N/A");
      }
    },

    isImageFile(url) {
      if (!url) return false;
      const imageExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".webp",
        ".svg",
      ];
      const lowerUrl = url.toLowerCase();
      return imageExtensions.some((ext) => lowerUrl.includes(ext));
    },

    getFileName(url) {
      if (!url) return "Unknown File";
      const parts = url.split("/");
      return parts[parts.length - 1] || "Unknown File";
    },

    openImageLightbox(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.showImageModal = true;
      // Auto-close the view modal when opening image lightbox
      this.showModal = false;
    },

    // Note modal methods
    openNoteModal(note, title, item) {
      this.selectedNote = note;
      this.selectedNoteTitle = title;
      this.selectedNoteItem = item;
      this.showNoteModal = true;
    },

    // Attachment modal methods
    openAttachmentModal(attachmentString) {
      if (!attachmentString) return;

      // Split the attachment string by comma and filter out empty strings
      this.selectedAttachments = attachmentString
        .split(",")
        .filter((url) => url.trim());
      this.currentImageIndex = 0;
      this.imageLoading = true;
      this.showAttachmentModal = true;

      // Preload all images for faster switching
      this.preloadImages();

      // Add keyboard event listener for navigation
      this.$nextTick(() => {
        document.addEventListener("keydown", this.handleKeydown);
      });
    },

    handleKeydown(event) {
      if (this.showAttachmentModal) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.prevImage();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          this.nextImage();
        }
      }
    },

    closeAttachmentModal() {
      this.selectedAttachments = [];
      this.currentImageIndex = 0;
      this.imageLoading = false;
      this.imagesPreloaded = false;
      // Remove keyboard event listener
      document.removeEventListener("keydown", this.handleKeydown);
    },

    preloadImages() {
      let loadedCount = 0;
      const totalImages = this.selectedAttachments.filter((url) =>
        this.isImageFile(url)
      ).length;

      if (totalImages === 0) {
        this.imageLoading = false;
        this.imagesPreloaded = true;
        return;
      }

      this.selectedAttachments.forEach((url, index) => {
        if (this.isImageFile(url)) {
          const img = new Image();
          img.onload = () => {
            loadedCount++;
            // Only hide loading when all images are loaded or current image is loaded
            if (
              loadedCount === totalImages ||
              index === this.currentImageIndex
            ) {
              this.imageLoading = false;
            }
            if (loadedCount === totalImages) {
              this.imagesPreloaded = true;
            }
          };
          img.onerror = () => {
            loadedCount++;
            if (
              loadedCount === totalImages ||
              index === this.currentImageIndex
            ) {
              this.imageLoading = false;
            }
            if (loadedCount === totalImages) {
              this.imagesPreloaded = true;
            }
          };
          img.src = url;
        } else {
          // For non-image files, don't show loading
          if (index === this.currentImageIndex) {
            this.imageLoading = false;
          }
        }
      });
    },

    getAttachmentCount(attachmentString) {
      if (!attachmentString) return 0;
      return attachmentString.split(",").filter((url) => url.trim()).length;
    },

    getAttachmentList(attachmentString) {
      if (!attachmentString) return [];
      return attachmentString.split(",").filter((url) => url.trim());
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    // Image navigation methods
    switchToImage(index) {
      if (index !== this.currentImageIndex) {
        // Show brief loading indicator
        this.imageLoading = true;
        this.currentImageIndex = index;

        // Hide loading after a very short delay (100ms) for smooth UX
        setTimeout(() => {
          this.imageLoading = false;
        }, 100);
      }
    },

    nextImage() {
      if (this.currentImageIndex < this.selectedAttachments.length - 1) {
        this.switchToImage(this.currentImageIndex + 1);
      }
    },

    prevImage() {
      if (this.currentImageIndex > 0) {
        this.switchToImage(this.currentImageIndex - 1);
      }
    },

    // Print methods - open new window tab like reconcilation.vue and reports.vue
    async openPrintModal() {
      // Fetch all data with page size 2000 for printing
      await this.loadAllLeaveRequestsForPrint();
      this.printLeaveReport();
    },

    async loadAllLeaveRequestsForPrint() {
      try {
        // Build URL for pagination API with page size 2000
        let url = this.$store.state.domain + "LeaveRequests/GetByPagination?";

        // Required parameters
        url += "db=" + this.$store.state.userData.db;

        // Use user's campus ID
        const campusID = this.$store.state.userData.cId;
        url += "&cID=" + campusID;

        // Add userID parameter
        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.localID ||
          1;
        url += "&userID=" + userID;

        // Add loadAll parameter - true if cID is 1, false otherwise
        const loadAll = campusID === 1 ? "true" : "false";
        url += "&loadAll=" + loadAll;

        // Add dt parameter (today's date in YYYY-MM-DD format)
        const today = new Date();
        const dt = today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
        url += "&dt=" + dt;

        // Pagination parameters - get all data
        url += "&pageNumber=1";
        url += "&pageSize=2000";

        // Filter parameters
        if (this.filters.search && this.filters.search.trim() !== "") {
          url += "&search=" + encodeURIComponent(this.filters.search.trim());
        }
        if (this.filters.dateFrom) {
          url += "&dateFrom=" + new Date(this.filters.dateFrom).toISOString();
        }
        if (this.filters.dateTo) {
          url += "&dateTo=" + new Date(this.filters.dateTo).toISOString();
        }

        // Sort parameters
        url += "&sortBy=" + this.filters.sortBy;
        url += "&sortOrder=" + this.filters.sortOrder;

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };

        let res = await this.get(obj);

        // Handle paginated response
        if (res && res.data && res.data.data) {
          this.leaveRequests = res.data.data || [];
          console.log(
            "All leave requests loaded for print:",
            this.leaveRequests.length,
            "records"
          );
        } else {
          this.leaveRequests = [];
          console.log("No leave requests data received for print");
        }
      } catch (error) {
        console.error("Error loading all leave requests for print:", error);
        this.leaveRequests = [];
      }
    },

    printLeaveReport() {
      console.log("Generating leave requests print report...");

      // Create the HTML content for the print view
      const printContent = this.generatePrintableHTML();

      // Open new window/tab
      const newWindow = window.open(
        "",
        "_blank",
        "width=1200,height=800,scrollbars=yes,resizable=yes"
      );

      if (newWindow) {
        newWindow.document.write(printContent);
        newWindow.document.close();

        // Add print functionality after the content is loaded
        newWindow.onload = function () {
          // Add print button functionality
          const printBtn = newWindow.document.getElementById("printButton");
          if (printBtn) {
            printBtn.addEventListener("click", function () {
              newWindow.print();
            });
          }
        };
      } else {
        alert(
          "Pop-up blocked! Please allow pop-ups for this site to view the leave requests report."
        );
      }
    },

    generatePrintableHTML() {
      // Calculate totals
      const totalRecords = this.leaveRequests.length;
      const pendingCount = this.leaveRequests.filter(
        (item) => item.status === "Pending"
      ).length;
      const approvedCount = this.leaveRequests.filter(
        (item) => item.status === "Approved"
      ).length;
      const rejectedCount = this.leaveRequests.filter(
        (item) => item.status === "Rejected"
      ).length;

      // Generate table rows
      const tableRows = this.leaveRequests
        .map(
          (item, index) => `
        <tr ${index % 2 === 0 ? 'class="even-row"' : ""}>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            index + 1
          }</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            item.tID || "N/A"
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            item.name
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            item.designation || "N/A"
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            item.department || "N/A"
          }</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <span class="badge badge-warning">${item.leaveType}</span>
          </td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${this.formatDate(
            item.dtFrom
          )}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${this.formatDate(
            item.dtTo
          )}</td>
          <td class="text-center highlight-days" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            item.totalDays
          } days</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <span class="badge ${this.getStatusBadgeClass(item.status)}">${
            item.status
          }</span>
          </td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${this.getApprovedRejectedBy(
            item
          )}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${this.formatDateTime(
            item.date
          )}</td>
        </tr>
      `
        )
        .join("");

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Leave Requests Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.4;
              color: #333;
              background-color: #fff !important;
              padding: 15px;
              font-size: 12px;
              margin: 0;
              width: 100%;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 10px;
              padding: 8px;
              border-radius: 6px;
              color: #212529;
              background: #f8f9fa;
            }
            
            .print-header h1 {
              margin: 0 0 5px 0;
              color: #212529;
              font-size: 20px;
            }
            
            .print-header p {
              font-size: 14px;
              opacity: 0.9;
              margin: 3px 0;
            }
            
            .school-info {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 20px;
              border-left: 4px solid #667eea;
            }
            
            .school-info h3 {
              color: #667eea;
              margin-bottom: 10px;
              font-size: 18px;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 10px;
              margin-bottom: 15px;
            }
            
            .info-item {
              padding: 8px 12px;
              background: white;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            
            .info-label {
              font-weight: bold;
              color: #495057;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .info-value {
              color: #667eea;
              font-weight: 600;
              font-size: 14px;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              background: white;
              border: 1px solid #ccc !important;
              border-spacing: 0 !important;
            }
            
            .report-table th {
              background-color: #e9ecef !important;
              color: #212529 !important;
              padding: 6px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #ccc !important;
            }
            
            .report-table td {
              padding: 6px 8px;
              border: 1px solid #ccc !important;
              font-size: 12px;
              vertical-align: middle;
            }
            
            .report-table tr.even-row {
              background-color: #f8f9fa;
            }
            
            .report-table tr:hover {
              background-color: #e3f2fd;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .highlight-days {
              background-color: #f8f9fa !important;
              color: #495057;
              font-weight: bold;
            }
            
            .badge {
              padding: 4px 6px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 600;
            }
            
            .badge-warning {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .badge-success {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .badge-danger {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            
            .summary-section {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-top: 20px;
              border: 1px solid #e9ecef;
            }
            
            .summary-title {
              color: #667eea;
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 15px;
              text-align: center;
            }
            
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              max-width: 800px;
              margin: 0 auto;
            }
            
            .summary-card {
              background: white;
              padding: 12px;
              border-radius: 6px;
              text-align: center;
              border: 1px solid #e9ecef;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            
            .summary-card .label {
              font-size: 10px;
              color: #6c757d;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 5px;
              font-weight: 500;
            }
            
            .summary-card .value {
              font-size: 14px;
              font-weight: bold;
              color: #667eea;
            }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #6c757d;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 13px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
            }
            
            .footer {
              text-align: center;
              margin-top: 30px;
              padding: 20px;
              background: #f8f9fa;
              border-radius: 8px;
              color: #6c757d;
              font-size: 11px;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                width: 100% !important;
                height: 100% !important;
                font-size: 11px !important;
              }
              
              .print-controls {
                display: none !important;
              }
              
              .print-header {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .report-table th {
                background-color: #e9ecef !important;
                color: #212529 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .highlight-days {
                background-color: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-success {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-warning {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-danger {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              @page {
                size: A4 landscape;
                margin: 0.5in;
                background: white !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="background: white !important; width: 100%; min-height: 100vh; margin: 0; padding: 0;">
            <!-- Print Controls -->
            <div class="print-controls">
              <button id="printButton" class="print-btn">
                🖨️ Print Report
              </button>
            </div>
            
            <!-- Header -->
            <div class="print-header">
              <h1>Leave Requests Report</h1>
              <p>Generated on ${new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}</p>
          </div>
          
          
          <!-- Main Table -->
          <table class="report-table" style="border: 1px solid #ccc !important; border-collapse: collapse !important; border-spacing: 0 !important;">
            <thead>
              <tr>
                <th class="text-center" style="width: 4%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">S.NO</th>
                <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Employee ID</th>
                <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Staff Name</th>
                <th class="text-left" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Designation</th>
                <th class="text-left" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Department</th>
                <th class="text-center" style="width: 8%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Leave Type</th>
                <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">From Date</th>
                <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">To Date</th>
                <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Days</th>
                <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Status</th>
                <th class="text-center" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Approved/Rejected By</th>
                <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Request Date</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          
          </div>
        </body>
        </html>
      `;
    },

    // Add modal methods
    openAddModal() {
      this.resetAddForm();
      // Show modal immediately
      this.showAddModal = true;
      // Load departments in the background (non-blocking)
      this.loadDepartmentsForAddModal();
    },

    async loadDepartmentsForAddModal() {
      this.addModalDepartmentLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db;

        if (this.addForm.campusID) {
          url += "&cId=" + this.addForm.campusID;
        } else {
          // Fallback to user's campus if no campus ID
          url += "&cId=" + this.$store.state.userData.cId;
        }

        console.log("Loading departments for add modal with URL:", url);

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };
        const departments = await this.get(obj);
        this.addModalDepartmentOptions = departments || [];
        console.log(
          "Add modal departments loaded:",
          this.addModalDepartmentOptions.length,
          "departments"
        );
      } catch (error) {
        console.error("Error loading departments for add modal:", error);
        this.addModalDepartmentOptions = [];
      } finally {
        this.addModalDepartmentLoading = false;
      }
    },

    async loadStaffForAddModal() {
      this.addModalStaffLoading = true;
      try {
        // Use the pagination API for staff loading
        let url = this.$store.state.domain + "staff/LoadDataPagination?";

        // Required parameters
        url += "db=" + this.$store.state.userData.db;

        // Use the campus ID from addForm
        const targetCampusID = this.addForm.campusID;

        if (targetCampusID) {
          url += "&cId=" + targetCampusID;
        } else {
          // Fallback to user's campus if no campus ID
          url += "&cId=" + this.$store.state.userData.cId;
        }

        // Add department filter if selected
        if (this.addForm.departmentID) {
          url += "&dptID=" + this.addForm.departmentID;
        }

        // Add other required parameters
        url += "&status=present"; // Only show present staff
        url += "&page=1"; // First page
        url += "&appointedFrom="; // Empty for no filter
        url += "&appointedTo="; // Empty for no filter
        url += "&designation="; // Empty for no filter
        url += "&sortBy=name"; // Sort by name
        url += "&sortOrder=asc"; // Ascending order

        console.log("Loading staff for add modal with URL:", url);

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Handle paginated response
        if (response && response.data && response.data.data) {
          this.addModalStaffOptions = response.data.data.filter(
            (staff) => staff.isActive !== false
          );
          console.log(
            "Add modal staff options loaded:",
            this.addModalStaffOptions.length,
            "staff members"
          );
        } else if (response && Array.isArray(response)) {
          // Fallback for non-paginated response
          this.addModalStaffOptions = response.filter(
            (staff) => staff.isActive !== false
          );
          console.log(
            "Add modal staff options loaded (fallback):",
            this.addModalStaffOptions.length,
            "staff members"
          );
        } else {
          console.log("No staff data received from API for add modal");
          this.addModalStaffOptions = [];
        }
      } catch (error) {
        console.error("Error loading staff for add modal:", error);
        this.$bvToast.toast(this.$t("Error loading staff data!"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.addModalStaffOptions = [];
      } finally {
        this.addModalStaffLoading = false;
      }
    },

    resetAddForm() {
      // Set campus ID based on user type
      const campusID = this.isHeadOfficeUser
        ? this.filters.campusID ||
          (this.$store.state.userData && this.$store.state.userData.cId)
        : this.$store.state.userData && this.$store.state.userData.cId;

      this.addForm = {
        id: 0,
        tID: null,
        departmentID: null,
        leaveType: null,
        leaveTypeOption: null,
        dtFrom: null,
        dtTo: null,
        dateRange: null,
        totalDays: 0,
        note: "",
        attachment: "",
        campusID: campusID || 0,
      };
      this.addFormErrors = {
        staffID: "",
        leaveType: "",
        dtFrom: "",
        dtTo: "",
        dateRange: "",
        note: "",
      };

      // Clear add modal specific dropdowns
      this.addModalDepartmentOptions = [];
      this.addModalStaffOptions = [];
      this.addModalDepartmentLoading = false;
      this.addModalStaffLoading = false;
      this.clearAddFormAttachmentInput();

      // Reload leave types without staff when dynamic balance is on
      if (this.isDynamicLeaveBalance) {
        this.loadLeaveTypes(null);
      }
    },

    async onStaffSelect(selectedStaffId) {
      this.addFormErrors.staffID = "";
      this.addForm.leaveType = null;
      this.addForm.leaveTypeOption = null;
      if (this.isDynamicLeaveBalance) {
        if (selectedStaffId) {
          await this.loadLeaveTypes(selectedStaffId);
        } else {
          this.leaveTypeOptions = [];
          this.leaveTypeSelectKey += 1;
        }
      }
    },

    onAddModalCampusChange() {
      // Clear dependent selections when campus changes
      this.addForm.departmentID = null;
      this.addForm.tID = null;
      this.addForm.leaveType = null;
      this.addForm.leaveTypeOption = null;
      this.addModalDepartmentOptions = [];
      this.addModalStaffOptions = [];
      this.addModalDepartmentLoading = false;
      this.addModalStaffLoading = false;

      // Load departments for the selected campus
      this.loadDepartmentsForAddModal();
      if (this.isDynamicLeaveBalance) {
        this.loadLeaveTypes(null);
      }
    },

    async onAddModalDepartmentChange() {
      // Clear staff selection when department changes
      this.addForm.tID = null;
      this.addForm.leaveType = null;
      this.addForm.leaveTypeOption = null;
      this.addModalStaffOptions = [];
      this.addModalStaffLoading = false;

      // Load staff for the selected department
      this.loadStaffForAddModal();
      if (this.isDynamicLeaveBalance) {
        this.loadLeaveTypes(null);
      }
    },

    onDateChange() {
      this.calculateTotalDays();
      this.addFormErrors.dtFrom = "";
      this.addFormErrors.dtTo = "";
    },

    onAddFormDateRangeChange(selectedDates, dateStr, instance) {
      if (selectedDates && selectedDates.length === 2) {
        this.addForm.dtFrom = selectedDates[0];
        this.addForm.dtTo = selectedDates[1];
        this.calculateTotalDays();
        this.addFormErrors.dateRange = "";
        this.addFormErrors.dtFrom = "";
        this.addFormErrors.dtTo = "";
      } else {
        this.addForm.dtFrom = null;
        this.addForm.dtTo = null;
        this.addForm.totalDays = 0;
      }
    },

    calculateTotalDays() {
      if (this.addForm.dtFrom && this.addForm.dtTo) {
        const fromDate = new Date(this.addForm.dtFrom);
        const toDate = new Date(this.addForm.dtTo);

        if (toDate >= fromDate) {
          const timeDiff = toDate.getTime() - fromDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
          this.addForm.totalDays = daysDiff;
        } else {
          this.addForm.totalDays = 0;
        }
      } else {
        this.addForm.totalDays = 0;
      }
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;

      // Convert date string to ISO 8601 format with timezone
      const date = new Date(dateString);
      return date.toISOString();
    },

    async handleAddFormAttachmentChange(event) {
      const file = event.target.files && event.target.files[0];
      this.addFormAttachmentFile = file || null;
      if (file) {
        await this.onFileChange(event);
      }
    },

    clearAddFormAttachmentInput() {
      this.addFormAttachmentFile = null;
      if (this.$refs.addFormAttachmentInput) {
        this.$refs.addFormAttachmentInput.value = "";
      }
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$bvToast.toast(this.$t("File size should not exceed 5MB"), {
            title: this.$t("File Size Error"),
            variant: "danger",
            solid: true,
          });
          this.clearAddFormAttachmentInput();
          return;
        }

        // Validate file type
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];
        if (!allowedTypes.includes(file.type)) {
          this.$bvToast.toast(
            "Please select a valid file type (PDF, DOC, DOCX, JPG, JPEG, PNG)",
            {
              title: this.$t("File Type Error"),
              variant: "danger",
              solid: true,
            }
          );
          this.clearAddFormAttachmentInput();
          return;
        }

        // Upload file to MySkool API
        await this.uploadFileToMySkool(file);
      }
    },

    async uploadFileToMySkool(file) {
      try {
        this.addLoading = true;
        const uploaded = await uploadToMySkool([file]);
        const path = getUploadPath(uploaded[0]);

        if (path) {
          this.addForm.attachment = path;

          this.$bvToast.toast(this.$t("File uploaded successfully"), {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
        } else {
          throw new Error("Invalid response from upload service");
        }
      } catch (error) {
        console.error("File upload error:", error);
        this.addForm.attachment = "";
        this.$bvToast.toast(this.$t("Failed to upload file"), {
          title: this.$t("Upload Error"),
          variant: "danger",
          solid: true,
        });
        this.clearAddFormAttachmentInput();
      } finally {
        this.addLoading = false;
      }
    },

    validateAddForm() {
      let isValid = true;
      this.addFormErrors = {
        staffID: "",
        leaveType: "",
        dtFrom: "",
        dtTo: "",
        dateRange: "",
        note: "",
      };

      if (!this.addForm.tID) {
        this.addFormErrors.staffID = this.$t("Please select a staff member");
        isValid = false;
      }

      if (!this.addForm.leaveType) {
        this.addFormErrors.leaveType = this.$t("Please select a leave type");
        isValid = false;
      }

      if (!this.addForm.dtFrom || !this.addForm.dtTo) {
        this.addFormErrors.dateRange = this.$t("Please select a date range");
        isValid = false;
      }

      if (this.addForm.dtFrom && this.addForm.dtTo) {
        const fromDate = new Date(this.addForm.dtFrom);
        const toDate = new Date(this.addForm.dtTo);

        if (toDate < fromDate) {
          this.addFormErrors.dateRange = this.$t(
            "End date must be after start date"
          );
          isValid = false;
        }
      }

      return isValid;
    },

    async submitAddLeave() {
      if (!this.validateAddForm()) {
        this.$bvToast.toast(
          this.$t("Please fill all required fields correctly"),
          {
            title: this.$t("Validation Error"),
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      this.addLoading = true;
      try {
        // Prepare request body as JSON object
        const requestBody = {
          id: this.addForm.id,
          tID: this.addForm.tID,
          leaveType: this.addForm.leaveType,
          dtFrom: this.formatDateForAPI(this.addForm.dtFrom),
          dtTo: this.formatDateForAPI(this.addForm.dtTo),
          totalDays: this.addForm.totalDays,
          note: this.addForm.note || "",
          campusID: this.addForm.campusID,
        };

        // Add attachment if uploaded
        if (this.addForm.attachment) {
          requestBody.attachment = this.addForm.attachment;
        }

        const obj = {
          url: `${this.$store.state.domain}LeaveRequests/Save?db=${this.$store.state.userData.db}&userID=${this.$store.state.userData.userID}&cID=${this.addForm.campusID}`,
          body: requestBody,
          token: this.$store.state.userData.token,
        };

        console.log("Leave Request Save URL:", obj.url);
        console.log("Leave Request Save Payload:", requestBody);
        console.log("Full Request Object:", obj);

        // Use axios directly to avoid store toast issues
        const axios = require("axios");
        const response = await axios.post(obj.url, obj.body, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
            "Content-Type": "application/json",
          },
        });
        const responseData = response.data;

        console.log("API Response:", responseData);
        console.log("Response status:", responseData?.status);
        console.log("Response message:", responseData?.message);
        console.log("Response data:", responseData?.data);

        // Check for success - handle different response structures
        const isSuccess =
          responseData &&
          (responseData.status === "success" ||
            responseData.status === "Success" ||
            responseData.message ||
            (responseData.data && responseData.data.status === "Pending") ||
            (responseData.data && responseData.data.status === "Approved") ||
            (responseData.data && responseData.data.status === "Rejected") ||
            (responseData.data && responseData.data.id) ||
            (responseData.data && responseData.data.tID));

        console.log("Is Success:", isSuccess);
        console.log("Success check details:", {
          hasResponse: !!responseData,
          statusSuccess: responseData?.status === "success",
          hasMessage: !!responseData?.message,
          hasDataId: !!(responseData?.data && responseData?.data.id),
          hasDataTID: !!(responseData?.data && responseData?.data.tID),
          dataStatus: responseData?.data?.status,
        });

        if (isSuccess) {
          this.$bvToast.toast(
            responseData.message ||
              this.$t("Leave request has been created successfully."),
            {
              title: this.$t("Success!"),
              variant: "success",
              solid: true,
            }
          );
          this.showAddModal = false;
          this.loadLeaveRequests(); // Refresh the list
        } else {
          // Additional fallback check for wrapped responses
          const wrappedResponse =
            responseData?.data || responseData?.response || responseData;
          const isWrappedSuccess =
            wrappedResponse &&
            (wrappedResponse.status === "success" ||
              wrappedResponse.message ||
              wrappedResponse.id ||
              wrappedResponse.tID);

          console.log("Wrapped response check:", {
            wrappedResponse,
            isWrappedSuccess,
          });

          if (isWrappedSuccess) {
            this.$bvToast.toast(
              wrappedResponse.message ||
                this.$t("Leave request has been created successfully."),
              {
                title: this.$t("Success!"),
                variant: "success",
                solid: true,
              }
            );
            this.showAddModal = false;
            this.loadLeaveRequests(); // Refresh the list
          } else {
            console.error("API Response:", responseData);
            this.$bvToast.toast(
              this.$t(
                "Failed to create leave request. Please check the console for details."
              ),
              {
                title: this.$t("Error!"),
                variant: "danger",
                solid: true,
              }
            );
          }
        }
      } catch (error) {
        console.error("Error creating leave request:", error);
        console.error("Error details:", error.response);

        let errorMessage = this.$t(
          "An error occurred while creating the leave request."
        );
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = this.$t(
              "API endpoint not found. Please contact the administrator."
            );
          } else if (error.response.status === 400) {
            errorMessage = this.$t(
              "Invalid request data. Please check your input."
            );
          } else if (error.response.status === 500) {
            errorMessage = this.$t("Server error. Please try again later.");
          }
        }

        this.$bvToast.toast(errorMessage, {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.addLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Keyframe animations - Soft landing */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Leave Request Card Styling (matching marks-entry-card)
.leave-request-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Exclude campus filter from general v-select styles
  ::v-deep .v-select:not(.campus-filter-select) {
    position: relative;
    z-index: 10;
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__dropdown-toggle {
    border-radius: 12px;
    min-height: 48px;
    border: 2px solid #e0d8ff;
    background: #fff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08);
    position: relative;
    z-index: 10;

    &:hover {
      border-color: #b8a9ff;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.12);
    }
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__dropdown-menu {
    border-radius: 12px;
    border: 2px solid #e0d8ff;
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
    background: #ffffff !important;
    z-index: 9999 !important;
    position: fixed !important;
    margin-top: 4px;
    max-height: 300px;
    overflow-y: auto;
    min-width: 200px;
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__dropdown-option {
    padding: 0.75rem 1rem;
    color: #2d1b69 !important;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
      opacity: 1 !important;
    }

    &--highlight {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600;
      opacity: 1 !important;
    }
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__selected {
    color: #2d1b69 !important;
    font-weight: 600;
    background: transparent !important;
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__search {
    color: #2d1b69 !important;
    font-weight: 500;
    padding: 0.5rem;
    background: transparent !important;
  }

  ::v-deep .v-select:not(.campus-filter-select) .vs__clear {
    color: #6b7280;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #7c5dfa;
    }
  }

  label {
    font-weight: 700;
    color: #2d1b69;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  // Remove any background from campus filter column
  > [class*="col-"]:first-child {
    background: transparent !important;
  }

  // Ensure buttons column is right-aligned
  > [class*="col-"].text-right {
    justify-content: flex-end;
  }
}

.filter-row {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .b-form-group {
    margin-bottom: 0;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #5b21b6;
      margin-bottom: 0.5rem;
      display: block;
      text-align: left;
    }
  }

  .v-select {
    width: 100%;

    .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.5rem 0.75rem;
      min-height: 42px;
      background: #fff;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    ::v-deep .vs__dropdown-menu {
      border-radius: 12px;
      border: 2px solid #e0d8ff;
      box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
      background: #ffffff !important;
      z-index: 9999 !important;
      position: fixed !important;
      margin-top: 4px;
      max-height: 300px;
      overflow-y: auto;
      min-width: 200px;
    }

    ::v-deep .vs__dropdown-option {
      padding: 0.75rem 1rem;
      color: #2d1b69 !important;
      font-weight: 500;
      transition: all 0.2s ease;
      cursor: pointer;
      background-color: #ffffff !important;
      background-image: none !important;
      opacity: 1 !important;
      visibility: visible !important;

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
        opacity: 1 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600;
        opacity: 1 !important;
      }
    }

    .vs__selected {
      margin: 0;
      padding: 0;
      border: none;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;
    }

    .vs__search {
      margin: 0;
      padding: 0;
      color: #2d1b69 !important;
      font-weight: 500;
      background: transparent !important;

      &::placeholder {
        color: #9ca3af;
        font-weight: 400;
      }
    }
  }

  .form-control {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.5rem 0.75rem;
    min-height: 42px;
    background: #fff;
    transition: all 0.2s ease;
    color: #2d1b69;
    font-weight: 500;
    flex: 1;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }
  }

  // Date range with clear button wrapper
  .d-flex.gap-2 {
    .form-control {
      flex: 1;
    }
  }
}

// Filter Control - Consistent height and styling for all filters
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

// v-select filter control styling
.filter-control.v-select {
  width: 100%;

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 10px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.5rem 0.75rem !important;
    background: #fff !important;
    transition: all 0.2s ease;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }
  }
}

// Campus Filter Dropdown - Fix Double Layer - Remove all background layers
.leave-request-card {
  // Remove background from column containing campus filter
  .filter-row-single-line > [class*="col-"]:first-child {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
  }
}

.leave-request-card .campus-filter-select {
  position: relative;
  z-index: 10;
  width: 100%;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;

  // Remove all wrapper backgrounds
  &::before,
  &::after {
    display: none !important;
  }

  ::v-deep .v-select {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;

    &::before,
    &::after {
      display: none !important;
    }
  }

  ::v-deep .vs__dropdown-toggle {
    background: #fff !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: none !important;
    border-radius: 10px !important;
    height: 42px !important;
    min-height: 42px !important;
    padding: 0.5rem 0.75rem !important;
    margin: 0 !important;

    &::before,
    &::after {
      display: none !important;
    }

    &:hover {
      border-color: #b8a9ff !important;
      box-shadow: none !important;
      background: #fff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
      background: #fff !important;
    }
  }

  // Remove any wrapper backgrounds
  ::v-deep .vs__dropdown-toggle > * {
    background: transparent !important;
  }

  ::v-deep .vs__selected-options {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__search-container {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__selected {
    background: transparent !important;
  }

  ::v-deep .vs__search {
    background: transparent !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 10000 !important;
    position: fixed !important;
    background: #ffffff !important;
    border: 2px solid #e0d8ff !important;
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15) !important;
    border-radius: 12px !important;
    margin-top: 4px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    min-width: 200px !important;

    .vs__dropdown-option {
      background-color: #ffffff !important;
      background-image: none !important;
      padding: 0.75rem 1rem !important;
      color: #2d1b69 !important;
      font-weight: 500 !important;

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600 !important;
      }
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    background: transparent !important;
  }

  ::v-deep .vs__search {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    background: transparent !important;

    &::placeholder {
      color: #9ca3af !important;
      font-weight: 400 !important;
    }
  }

  ::v-deep .vs__actions {
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__clear {
    margin: 0 !important;
    padding: 0 !important;
  }
}

// Page Size Dropdown
.page-size-group {
  margin-bottom: 0 !important;
  min-width: 90px;

  .page-size-select {
    ::v-deep .vs__dropdown-toggle {
      height: 38px !important;
      min-height: 38px !important;
      border-radius: 8px !important;
      border: 1.5px solid #e0d8ff !important;
      padding: 0.375rem 0.625rem !important;
      background: #fff !important;
      font-size: 0.875rem !important;

      &:hover {
        border-color: #b8a9ff !important;
      }

      &:focus-within {
        border-color: #7c5dfa !important;
        box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
      }
    }

    ::v-deep .vs__selected {
      margin: 0 !important;
      padding: 0 !important;
      color: #2d1b69 !important;
      font-weight: 600 !important;
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    ::v-deep .vs__selected-options {
      padding: 0 !important;
      display: flex !important;
      align-items: center !important;
    }

    ::v-deep .vs__search {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    ::v-deep .vs__actions {
      padding: 0 0.25rem 0 0.5rem !important;
    }

    ::v-deep .vs__dropdown-menu {
      z-index: 10000 !important;
      position: fixed !important;
      min-width: 90px !important;
      border-radius: 8px !important;
      border: 1.5px solid #e0d8ff !important;
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
      margin-top: 4px !important;
    }

    ::v-deep .vs__dropdown-option {
      padding: 0.5rem 0.75rem !important;
      color: #2d1b69 !important;
      font-weight: 500 !important;
      font-size: 0.875rem !important;

      &:hover {
        background-color: #f9fafb !important;
        color: #1f2937 !important;
      }

      &--highlight {
        background-color: #f3f4f6 !important;
        color: #1f2937 !important;
        font-weight: 600 !important;
      }
    }
  }
}

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }

  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }

  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

// Page Size Wrapper
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }

    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }

    // Close dropdown after click
    &:active {
      background-color: #e5e7eb !important;
    }

    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }

  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Switch
.pagination-switch-compact {
  margin-bottom: 0 !important;
  display: flex;
  align-items: center;
  white-space: nowrap;

  ::v-deep .custom-control-label {
    padding-left: 0.375rem;
    font-size: 0.8125rem;
    color: #64748b;
    cursor: pointer;
    font-weight: 500;
    margin-bottom: 0;
    white-space: nowrap;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label {
    color: #7c5dfa;
  }

  ::v-deep .custom-switch .custom-control-label::before {
    width: 1.75rem;
    height: 1rem;
    border-radius: 0.5rem;
    background-color: #cbd5e1;
    border-color: #cbd5e1;
  }

  ::v-deep .custom-switch .custom-control-label::after {
    width: calc(1rem - 4px);
    height: calc(1rem - 4px);
    border-radius: 50%;
    background-color: #fff;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::before {
    background-color: #7c5dfa;
    border-color: #7c5dfa;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::after {
    transform: translateX(0.75rem);
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

// Pagination Controls - Bottom (removed, but keeping for reference)
.pagination-controls {
  padding: 1rem 0;
  border-top: 1px solid #e5e7eb;

  .d-flex {
    gap: 0.75rem;
  }
}

// Pagination Switch
.pagination-switch {
  margin-bottom: 0 !important;
  display: flex;
  align-items: center;

  ::v-deep .custom-control-label {
    padding-left: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
    cursor: pointer;
    font-weight: 500;
    margin-bottom: 0;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label {
    color: #7c5dfa;
  }

  ::v-deep .custom-switch .custom-control-label::before {
    width: 2rem;
    height: 1.125rem;
    border-radius: 0.5625rem;
    background-color: #cbd5e1;
    border-color: #cbd5e1;
  }

  ::v-deep .custom-switch .custom-control-label::after {
    width: calc(1.125rem - 4px);
    height: calc(1.125rem - 4px);
    border-radius: 50%;
    background-color: #fff;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::before {
    background-color: #7c5dfa;
    border-color: #7c5dfa;
  }

  ::v-deep
    .custom-switch
    .custom-control-input:checked
    ~ .custom-control-label::after {
    transform: translateX(0.875rem);
  }
}

// Action Buttons
.action-btn {
  font-weight: 600;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Filter row action buttons - same height as filters
.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Clear Button - Circle
.clear-btn-circle {
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e0d8ff;
  background: #fff;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  flex-shrink: 0;

  &:hover {
    border-color: #ef4444;
    background: #fee2e2;
    color: #dc2626;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }
}

// Print Button - Icon Only
.print-btn-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  border-radius: 10px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #7c5dfa;
  background: #fff;
  color: #7c5dfa;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    background: #f3f0ff;
    border-color: #6d4cdb;
    color: #6d4cdb;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Outline Action Buttons (Approve/Reject) - White Background
.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;

  &.approve-btn {
    border: 2px solid #10b981 !important;
    color: #10b981 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);

    &:hover:not(:disabled) {
      background: #f0fdf4 !important;
      border-color: #059669 !important;
      color: #059669 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    }
  }

  &.reject-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

    &:hover:not(:disabled) {
      background: #fef2f2 !important;
      border-color: #dc2626 !important;
      color: #dc2626 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Modern Notes Display
.note-cell-modern {
  display: flex;
  align-items: center;
}

.note-badge-modern {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.625rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 100%;
  overflow: hidden;

  .note-icon {
    flex-shrink: 0;
    margin-right: 0.375rem;
  }

  .note-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.leave-note-modern {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;

  &:hover {
    background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
    color: #1e3a8a;
    border-color: #60a5fa;
  }
}

.admin-note-modern {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  color: #6b21a8;
  border: 1px solid #c084fc;

  &:hover {
    background: linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%);
    color: #581c87;
    border-color: #a78bfa;
  }
}

.attachment-cell-modern {
  display: flex;
  align-items: center;
}

.attachment-badge-modern {
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.625rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
}

.text-muted-empty {
  color: #94a3b8;
  font-size: 0.875rem;
}

// Leave Requests List Card
.leave-requests-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible; // Allow dropdowns to show
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem; // Reduced margin to touch curve
    right: 0.75rem; // Reduced margin to touch curve
    width: calc(100% - 1.5rem); // Subtract left and right padding
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Filter row styling inside card
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.requests-count-badge {
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
  }
}

// Table Wrapper for Horizontal Scroll
// Table Scroll Container
.table-scroll-container {
  position: relative;
  width: 100%;
}

// Top Scrollbar for PC Users
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  // Bottom scrollbar styling
  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  // Firefox scrollbar
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

// Leave Requests Table - Modern Compact Design
.leave-requests-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 3px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      td { background: #f8fafc; }
    }

    &:nth-child(odd) {
      background: #ffffff;
      td { background: #ffffff; }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;
      td {
        background: #f3f0ff !important;
        color: #2d1b69;
        font-weight: 500;
      }
    }
  }

  ::v-deep .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-weight: 600;
    line-height: 1.2;
  }

  // Compact cell content
  ::v-deep .text-muted {
    font-size: 0.8rem;
    color: #64748b;
  }
}

// Skeleton Loader Styles - Premium Animated Effect
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  // Add subtle border animation
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  // Premium shimmer effect
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  // Secondary glow layer
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

// Different widths for different column positions
.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 50px;
    margin: 0 auto;
  }

  &:nth-child(2) .skeleton-loader {
    width: 70px;
  }

  &:nth-child(3) .skeleton-loader {
    width: 140px;
  }

  &:nth-child(4) .skeleton-loader {
    width: 110px;
  }

  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(7) .skeleton-loader {
    width: 60px;
  }

  &:nth-child(8) .skeleton-loader {
    width: 90px;
  }

  &:nth-child(9) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(10) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(11) .skeleton-loader {
    width: 120px;
  }

  &:nth-child(12) .skeleton-loader {
    width: 100px;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

.gap-2 {
  gap: 0.5rem;
}

// Modern Modal Styling
::v-deep .modal {
  z-index: 1050;
}

::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

::v-deep .modal-dialog {
  overflow: visible !important;
}

::v-deep .modal-content {
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: visible;
  position: relative;
}

::v-deep .modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.5rem 4rem 1.5rem 2rem !important;
  border-radius: 20px 20px 0 0;
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  overflow: visible !important;
  min-height: 4.5rem !important;
  flex-direction: row !important;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    z-index: 1;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    position: relative;
    z-index: 2;
    flex: 1;
    padding-right: 1.5rem;
    margin-right: 0.5rem;
    order: 1 !important;
    text-align: left !important;
    margin-left: 0 !important;
  }

  // Force close button to be positioned absolutely on the right
  // Bootstrap Vue renders close button as first child, but we override with absolute positioning
  > button:first-child:not(.modal-title),
  > .close:first-child {
    order: 999 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    // These will be overridden by the more specific close button styles below
  }
}

// Modern Close Button - Revamped Design
// Target ALL possible close button selectors with maximum specificity
// Override global modal styles that position close button on the left
// Using multiple selectors to ensure highest specificity
.view-details-modal .modal-header .close,
.add-leave-modal .modal-header .close,
.approve-modal-compact .modal-header .close,
.reject-modal-compact .modal-header .close,
.modal .modal-header .close,
.modal .modal-header button.close,
.modal .modal-header .btn-close,
.modal .modal-header button[aria-label="Close"],
.modal .modal-header .modal-header-close,
.modal .modal-header button[type="button"].close,
.modal .modal-header > button:first-child:not(.modal-title),
.modal .modal-header > .close:first-child,
::v-deep .view-details-modal .modal-header .close,
::v-deep .add-leave-modal .modal-header .close,
::v-deep .approve-modal-compact .modal-header .close,
::v-deep .reject-modal-compact .modal-header .close,
::v-deep .modal .modal-header .close,
::v-deep .modal .modal-header button.close,
::v-deep .modal .modal-header .btn-close,
::v-deep .modal .modal-header button[aria-label="Close"],
::v-deep .modal .modal-header .modal-header-close,
::v-deep .modal .modal-header button[type="button"].close,
::v-deep .modal .modal-header > button:first-child:not(.modal-title),
::v-deep .modal .modal-header > .close:first-child,
::v-deep .modal-header .close,
::v-deep .modal-header button.close,
::v-deep .modal-header .btn-close,
::v-deep .modal-header button[aria-label="Close"],
::v-deep .modal-header .modal-header-close,
::v-deep .modal-header button[type="button"].close,
::v-deep .modal-header > button:first-child:not(.modal-title),
::v-deep .modal-header > .close:first-child {
  // Reset ALL positioning from global styles
  margin: 0 !important;
  padding: 0 !important;

  // Override global transform that moves button to left
  transform: none !important;
  translate: none !important;

  // Modern button dimensions
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  max-width: 2.75rem !important;
  max-height: 2.75rem !important;

  // Flexbox centering
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  // CRITICAL: Absolute positioning on the RIGHT side
  z-index: 100 !important;

  // Modern styling
  border-radius: 12px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f7ff 100%) !important;
  border: 2px solid #e8e4ff !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05) !important;

  // Smooth transitions
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;

  // Remove default button styles
  float: none !important;
  order: 999 !important;

  // Icon/X styling
  span,
  &::before,
  &::after {
    font-size: 1.25rem !important;
    line-height: 1 !important;
    color: #7c5dfa !important;
    font-weight: 600 !important;
    display: block !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  // Hover state - Enhanced
  &:hover {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    border-color: #7c5dfa !important;
    box-shadow: 0 4px 16px rgba(124, 93, 250, 0.25),
      0 2px 8px rgba(124, 93, 250, 0.15) !important;
    transform: translateY(-2px) scale(1.05) !important;

    span,
    &::before,
    &::after {
      color: #ffffff !important;
      transform: rotate(90deg) !important;
    }
  }

  // Active state
  &:active {
    transform: translateY(0) scale(0.98) !important;
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.2) !important;
  }

  // Focus state - Accessibility
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.15),
      0 4px 16px rgba(124, 93, 250, 0.25) !important;
    transform: none !important;
  }

  // Focus-visible for keyboard navigation
  &:focus-visible {
    outline: 2px solid #7c5dfa !important;
    outline-offset: 2px !important;
  }
}

// Smooth entrance animation for close button when modal opens
::v-deep .modal.show .modal-header .close,
::v-deep .modal.show .modal-header button.close,
::v-deep .modal.show .modal-header .btn-close {
  animation: closeButtonFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both !important;
}

@keyframes closeButtonFadeIn {
  0% {
    opacity: 0;
    transform: translateX(10px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

// Exclude modal-title from button styling
::v-deep .modal-header .modal-title {
  position: relative !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  width: auto !important;
  height: auto !important;
  min-width: auto !important;
  min-height: auto !important;
  float: none !important;
  order: 1 !important;
}

::v-deep .modal-body {
  padding: 2rem;
  background: #ffffff;
}

::v-deep .modal-footer {
  background: #faf9ff;
  border-top: 1px solid #e0d8ff;
  padding: 1.25rem 2rem;
  border-radius: 0 0 20px 20px;

  .btn {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1.25rem;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

// View Details Modal - Modern Grid Layout
.view-details-modal {
  ::v-deep .modal-dialog {
    max-width: 900px;
  }
  ::v-deep .modal-body {
    padding: 2rem;
    background: #ffffff;
  }
}

// Premium Leave Request Modal
.lr-modal-content {
  border-radius: 20px !important;
  overflow: hidden;
  border: 1.5px solid #e0d8ff !important;
  box-shadow: 0 20px 60px rgba(115, 103, 240, 0.2) !important;
}

.lr-modal-body {
  padding: 1.5rem;
}

.lr-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20px;
    width: 180px;
    height: 180px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 50%;
  }
}

.lr-modal-header-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  position: relative;
  z-index: 1;
}

.lr-modal-icon-wrap {
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lr-modal-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.3px;
}

.lr-modal-subtitle {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

.lr-modal-close-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  position: relative;
  z-index: 1;
  &:hover { background: rgba(255, 255, 255, 0.25) !important; }
}

.lr-modal-footer-btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border: 1.5px solid #e0d8ff;
  color: #64748b;
  transition: all 0.2s ease;
  &:hover { background: #f8fafc; border-color: #b8a9ff; color: #7c5dfa; }
}

// Enhanced table cell styles
.lr-person-cell {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.lr-person-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.85rem;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(124, 93, 250, 0.25);
}

.lr-person-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.lr-person-name {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.875rem;
  line-height: 1.2;
}

.lr-person-sub {
  font-size: 0.72rem;
  color: #7c5dfa;
  font-weight: 600;
}

.lr-dept-cell, .lr-type-cell, .lr-date-cell {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.lr-cell-icon, .lr-type-icon, .lr-date-icon {
  color: #7c5dfa;
  flex-shrink: 0;
  opacity: 0.8;
}

.lr-type-cell { color: #d97706; }

.lr-days-chip {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
  color: #0369a1;
  border-radius: 20px;
  padding: 0.25em 0.75em;
  font-weight: 700;
  font-size: 0.8rem;
  border: 1px solid #bae6fd;
}

.lr-status-badge {
  font-size: 0.78rem !important;
  padding: 0.3em 0.8em !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
}

.lr-badge-pill {
  border-radius: 20px !important;
  font-size: 0.78rem !important;
}

.leave-details-section {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #f0edff;
  }

  .section-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
  }

  .section-title {
    font-size: 1.1rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.2px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.details-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
}

.details-meta-item {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border: 1.5px solid #f0edff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.12);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }
  }

  .details-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 1px;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .details-value {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: #2d1b69;
    line-height: 1.3;

    &.details-value-primary {
      color: #7c5dfa;
    }

    &.details-value-info {
      color: #06b6d4;
    }

    &.details-value-success {
      color: #10b981;
    }

    &.details-value-muted {
      color: #64748b;
      font-weight: 600;
    }
  }

  .details-badge {
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.4rem 0.9rem;
  }
}

.details-note-item,
.details-attachment-item {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0edff;

  .details-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 1px;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }
}

.details-note-content {
  background: #ffffff;
  border: 1.5px solid #f0edff;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  font-size: 0.95rem;
  color: #2d1b69;
  line-height: 1.6;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);
}

.details-attachment-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.details-attachment-wrapper {
  background: #ffffff;
  border: 1.5px solid #f0edff;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.12);
    border-color: #d4c7ff;
  }
}

.details-attachment-image {
  cursor: pointer;

  .details-attachment-preview {
    width: 100%;
    height: auto;
    max-height: 300px;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
}

.details-attachment-file {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;

  .details-file-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
    flex-shrink: 0;
  }

  .details-file-info {
    flex: 1;
    min-width: 0;

    .details-file-name {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: #2d1b69;
      word-break: break-word;
    }
  }
}

// Modern Add Leave Request Modal Styling
.add-leave-modal {
  ::v-deep .modal-dialog {
    max-width: 800px;
  }

  ::v-deep .modal-body {
    padding: 1.5rem 2rem;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.modern-form {
  .form-section {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e0d8ff;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  .section-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d1b69;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e0d8ff;
  }

  .form-row-modern {
    margin-bottom: 0;
  }

  .modern-form-group {
    margin-bottom: 1.25rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
    }
  }

  .modern-select {
    ::v-deep .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.625rem 0.875rem;
      min-height: 44px;
      background: #fff;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    &.is-invalid {
      ::v-deep .vs__dropdown-toggle {
        border-color: #ef4444;
      }
    }
  }

  .leave-type-field {
    .lt-picker {
      border: 1.5px solid #e0d8ff;
      border-radius: 12px;
      background: #fff;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.06);

      &--invalid {
        border-color: #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
      }

      &--placeholder {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.85rem 1rem;
        color: #64748b;
        font-size: 0.8125rem;
        font-weight: 500;
        background: #f8fafc;
        box-shadow: none;
      }
    }

    .lt-picker__ph-icon {
      color: #94a3b8;
      flex-shrink: 0;
    }

    .lt-picker__hint {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.65rem 0.85rem;
      font-size: 0.75rem;
      line-height: 1.45;
      color: #475569;
      background: linear-gradient(180deg, #faf8ff 0%, #fff 100%);
      border-bottom: 1px solid #ede9fe;

      svg {
        flex-shrink: 0;
        margin-top: 0.1rem;
        color: #7c5dfa;
      }
    }

    .lt-picker__loading,
    .lt-picker__empty {
      padding: 1.25rem 1rem;
      text-align: center;
      font-size: 0.8125rem;
      color: #64748b;
    }

    .lt-picker__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .lt-picker__scroll {
      max-height: 220px;
      overflow-y: auto;
    }

    .lt-picker__head,
    .lt-picker__row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 5.25rem 5.25rem 2rem;
      gap: 0.35rem 0.5rem;
      align-items: center;
      padding: 0.5rem 0.85rem;
    }

    .lt-picker__head {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #64748b;

      span:nth-child(2),
      span:nth-child(3),
      span:nth-child(4) {
        text-align: center;
      }
    }

    .lt-picker__row {
      position: relative;
      width: 100%;
      border: 0;
      border-bottom: 1px solid #f1f5f9;
      background: #fff;
      cursor: pointer;
      text-align: left;
      transition: background 0.15s ease;

      &:last-of-type {
        border-bottom: 0;
      }

      &:hover {
        background: #faf8ff;
      }

      &--active {
        background: #f5f3ff;
        box-shadow: inset 3px 0 0 #7c5dfa;
      }
    }

    .lt-picker__name {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #1e293b;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .lt-picker__cell {
      display: flex;
      justify-content: center;
    }

    .lt-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 2.25rem;
      padding: 0.2rem 0.5rem;
      border-radius: 6px;
      font-size: 0.8125rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      line-height: 1.2;
      color: #334155;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
    }

    .lt-pill--ok {
      color: #15803d;
    }

    .lt-pill--zero {
      color: #dc2626;
    }

    .lt-pill--total {
      color: #64748b;
      font-weight: 700;
    }

    .lt-picker__check-wrap {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.35rem;
      height: 1.35rem;
      border-radius: 50%;
      background: #7c5dfa;
      color: #fff;
    }

    .lt-picker__footer {
      padding: 0.75rem 0.85rem;
      background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
      border-top: 1px solid #e2e8f0;
    }

    .lt-picker__footer-title {
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #64748b;
      margin-bottom: 0.15rem;
    }

    .lt-picker__footer-name {
      font-size: 0.9375rem;
      font-weight: 700;
      color: #2d1b69;
      margin-bottom: 0.65rem;
    }

    .lt-picker__footer-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;
    }

    .lt-stat {
      text-align: center;
      padding: 0.45rem 0.35rem;
      border-radius: 8px;
      background: #fff;
      border: 1px solid #e2e8f0;
    }

    .lt-stat__label {
      display: block;
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #64748b;
      margin-bottom: 0.2rem;
    }

    .lt-stat__value {
      display: block;
      font-size: 1.125rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
    }

    .lt-stat--ok .lt-stat__value {
      color: #15803d;
    }

    .lt-stat--zero .lt-stat__value {
      color: #dc2626;
    }

    .lt-stat--neutral .lt-stat__value {
      color: #334155;
    }

    .lt-stat--ratio .lt-stat__value {
      font-size: 1rem;
      color: #7c5dfa;
    }

    .leave-type-dropdown {
      ::v-deep .vs__selected {
        opacity: 1 !important;
        position: relative !important;
        color: #1e293b !important;
        font-weight: 600;
      }
    }
  }

  .modern-input {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    min-height: 44px;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &[readonly] {
      background: #f8fafc;
      color: #64748b;
    }

    &.is-invalid {
      border-color: #ef4444;
    }
  }

  .modern-textarea {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.75rem 0.875rem;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;
    resize: vertical;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &.is-invalid {
      border-color: #ef4444;
    }

    &::placeholder {
      color: #94a3b8;
      font-weight: 400;
    }
  }

  .file-upload-modern {
    .leave-file-picker__field {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      min-height: 2.75rem;
      padding: 0.5rem 0.5rem 0.5rem 0.875rem;
      background: #fff;
      border: 1.5px solid #e0d8ff;
      border-radius: 10px;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    .leave-file-picker__name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.5;
      font-size: 0.875rem;
      font-weight: 500;
      color: #64748b;
    }

    .leave-file-picker__btn {
      flex-shrink: 0;
      white-space: nowrap;
      border: none;
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      font-weight: 600;

      &:hover:not(:disabled),
      &:focus:not(:disabled) {
        background: linear-gradient(135deg, #6b4de8 0%, #8c6aef 100%);
      }
    }
  }

  .form-text-modern {
    display: flex;
    align-items: center;
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
  }
}

.modal-footer-modern {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;

  .btn-modern-cancel {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1.5rem;
    border: 1.5px solid #e0d8ff;
    color: #64748b;
    background: #fff;
    transition: all 0.3s ease;

    &:hover {
      background: #f8fafc;
      border-color: #b8a9ff;
      color: #475569;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .btn-modern-primary {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1.5rem;
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
    border: none;
    color: #fff;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// Modal styling
.employee-card,
.leave-card,
.additional-card,
.employee-info-card,
.leave-info-card,
.approval-card {
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  padding: 1.25rem;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(115, 103, 240, 0.12);
    transform: translateY(-2px);
  }
}

// Compact modal styling with larger fonts for XL modal
.compact-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "";
    width: 3px;
    height: 1rem;
    background: linear-gradient(180deg, #7c5dfa 0%, #9d7aff 100%);
    border-radius: 2px;
  }
}

// Combined card styling
.combined-card,
.combined-bottom-card {
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;

  .border-right {
    border-right: 2px solid #e0d8ff;
    padding-right: 1.25rem;
    margin-right: 1rem;
  }
}

// Border right for approve/reject modals
.border-right {
  border-right: 2px solid #e0d8ff;
  padding-right: 1.5rem;
  margin-right: 1rem;
}

// Ultra compact styling for side-by-side layout with larger fonts
.info-item-ultra-compact {
  padding: 0.5rem 0;

  .info-label-ultra-compact {
    font-size: 0.7rem;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.375rem;
    display: block;
  }

  .info-value-ultra-compact {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0;
    line-height: 1.4;
    color: #1e293b;

    &.primary {
      color: #7c5dfa;
      font-weight: 700;
    }

    &.info {
      color: #17a2b8;
      font-weight: 600;
    }

    &.success {
      color: #28a745;
      font-weight: 700;
    }

    &.muted {
      color: #6c757d;
    }
  }
}

.badge-ultra-compact {
  font-size: 11px;
  padding: 3px 6px;
  border-radius: 8px;
}

.note-content-ultra-compact {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
  color: #495057;
  margin-top: 2px;
}

.attachment-content-ultra-compact {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item-ultra-compact {
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;

  &:last-child {
    margin-bottom: 0;
  }
}

.attachment-image-ultra-compact {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  .attachment-preview-ultra-compact {
    max-width: 120px;
    max-height: 80px;
    border-radius: 4px;
    border: 2px solid #e3e6f0;
    cursor: pointer;
    transition: all 0.3s ease;
    object-fit: cover;

    &:hover {
      border-color: #007bff;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
    }
  }
}

.attachment-file-ultra-compact {
  display: flex;
  align-items: center;
  padding: 6px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;

  .file-icon-ultra-compact {
    margin-right: 8px;
    color: #6c757d;
  }

  .file-info-ultra-compact {
    flex: 1;

    .file-name-ultra-compact {
      margin: 0;
      font-weight: 500;
      color: #495057;
      font-size: 12px;
    }
  }
}

// Legacy compact styling (keeping for other sections)
.info-item-compact {
  .info-label-compact {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1px;
    display: block;
  }

  .info-value-compact {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 0;

    &.primary {
      color: #007bff;
      font-weight: 600;
    }

    &.info {
      color: #17a2b8;
      font-weight: 600;
    }

    &.success {
      color: #28a745;
      font-weight: 700;
    }

    &.muted {
      color: #6c757d;
    }
  }
}

.badge-compact {
  font-size: 10px;
  padding: 3px 6px;
  border-radius: 10px;
}

.note-content-compact {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 6px;
  font-size: 12px;
  line-height: 1.3;
  color: #495057;
  margin-top: 1px;
}

.attachment-content-compact {
  margin-top: 2px;
}

.attachment-image-compact {
  .attachment-preview-compact {
    max-width: 100px;
    max-height: 60px;
    border-radius: 4px;
    border: 2px solid #e3e6f0;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #007bff;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
    }
  }
}

.attachment-file-compact {
  display: flex;
  align-items: center;
  padding: 4px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;

  .file-icon-compact {
    margin-right: 6px;
    color: #6c757d;
  }

  .file-info-compact {
    flex: 1;

    .file-name-compact {
      margin: 0;
      font-weight: 500;
      color: #495057;
      font-size: 11px;
    }
  }
}

.employee-avatar,
.leave-avatar,
.additional-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.employee-avatar {
  background: #e9ecef;
  color: #495057;
}

.leave-avatar {
  background: #e9ecef;
  color: #495057;
}

.additional-avatar {
  background: #e9ecef;
  color: #495057;
}

.approval-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  color: #495057;
}

.employee-avatar-small,
.leave-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  color: #495057;
}

.employee-avatar-small {
  background: #e9ecef;
  color: #495057;
}

.leave-avatar-small {
  background: #e9ecef;
  color: #495057;
}

.info-item {
  .info-label {
    font-size: 11px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
    display: block;
  }

  .info-value {
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 0;

    &.primary {
      color: #007bff;
      font-weight: 600;
    }

    &.info {
      color: #17a2b8;
      font-weight: 600;
    }

    &.success {
      color: #28a745;
      font-weight: 700;
    }

    &.muted {
      color: #6c757d;
    }
  }
}

.leave-type-badge {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 20px;
}

.status-badge {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.note-content {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
  color: #495057;
  margin-top: 2px;
}

.attachment-content {
  margin-top: 4px;
}

.attachment-image {
  .attachment-preview {
    max-width: 150px;
    max-height: 100px;
    border-radius: 6px;
    border: 2px solid #e3e6f0;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #007bff;
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    }
  }

  .attachment-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.attachment-file {
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;

  .file-icon {
    margin-right: 8px;
    color: #6c757d;
  }

  .file-info {
    flex: 1;

    .file-name {
      margin: 0 0 4px 0;
      font-weight: 500;
      color: #495057;
      font-size: 13px;
    }

    .file-actions {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }
  }
}

// Action buttons styling
.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Table styling
.table {
  .badge {
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 12px;
  }
}

// Note and Attachment Modal Styling
.note-badge {
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.leave-note {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1565c0;
  border-color: #90caf9;
}

.leave-note:hover {
  background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%);
  color: #0d47a1;
}

.admin-note {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  color: #7b1fa2;
  border-color: #ce93d8;
}

.admin-note:hover {
  background: linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%);
  color: #4a148c;
}

.note-modal-content {
  max-height: 500px;
  overflow-y: auto;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.note-header-info {
  padding: 15px 0;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}

.info-single-line {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #495057;
}

.info-text {
  line-height: 1.4;
}

.note-text-full {
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.attachment-modal-content {
  max-height: 80vh;
  overflow-y: auto;
  padding: 0;
}

.attachment-gallery {
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.current-image-container {
  flex: 1;
  min-height: 500px;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin: 0;
  position: relative;
  overflow: hidden;
  padding: 20px;
}

.image-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.attachment-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  object-fit: contain;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.attachment-image:hover {
  transform: scale(1.01);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.file-preview {
  padding: 40px;
  text-align: center;
}

/* Thumbnail navigation removed for cleaner look */

// Responsive adjustments
@media (max-width: 768px) {
  .current-image-container {
    min-height: 300px;
    max-height: 60vh;
    padding: 15px;
  }

  .attachment-image {
    max-height: 400px;
  }
}

// Approval/Reject Form Section Styling
.approval-form-section {
  margin-top: 1.5rem;

  .form-group {
    margin-bottom: 1.25rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
    }
  }

  .modern-input,
  .modern-textarea {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    min-height: 44px;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;
    width: 100%;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &.is-invalid {
      border-color: #ef4444;
    }
  }

  .modern-textarea {
    min-height: 120px;
    resize: vertical;
  }

  .leave-type-field {
    .lt-grid {
      display: grid;
      grid-template-columns: 1fr 4.5rem 4.5rem;
      gap: 0.5rem;
      align-items: center;
      width: 100%;
    }

    .lt-grid--head {
      padding: 0.35rem 0.75rem;
      font-size: 0.65rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #64748b;
      background: #f1f5f9;
      border-bottom: 1px solid #e2e8f0;
    }

    .lt-grid--row {
      font-size: 0.8125rem;
    }

    .lt-grid__name {
      font-weight: 600;
      color: #1e293b;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .lt-grid__rem {
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      padding: 0.15rem 0.35rem;
      border-radius: 4px;
    }

    .lt-grid__rem--ok {
      color: #15803d;
      background: #ecfdf3;
    }

    .lt-grid__rem--zero {
      color: #b91c1c;
      background: #fef2f2;
    }

    .lt-grid__tot {
      font-weight: 600;
      color: #475569;
      font-variant-numeric: tabular-nums;
    }

    .lt-selected-line {
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
      color: #1e293b;
      font-size: 0.875rem;
    }

    .lt-selected-ratio {
      font-weight: 800;
      font-variant-numeric: tabular-nums;
    }

    .lt-selected-ratio--ok {
      color: #15803d;
    }

    .lt-selected-ratio--zero {
      color: #dc2626;
    }

    .lt-balance-card {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      gap: 0.75rem;
      margin-top: 0.5rem;
      padding: 0.65rem 0.75rem;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
    }

    .lt-balance-card__item {
      text-align: center;
      min-width: 4.5rem;
    }

    .lt-balance-card__label {
      display: block;
      font-size: 0.65rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748b;
      margin-bottom: 0.15rem;
    }

    .lt-balance-card__value {
      display: block;
      font-size: 1.25rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      line-height: 1.1;
    }

    .lt-balance-card__value--ok {
      color: #15803d;
    }

    .lt-balance-card__value--zero {
      color: #dc2626;
    }

    .lt-balance-card__value--total {
      color: #334155;
    }

    .lt-balance-card__sep {
      font-size: 1.25rem;
      font-weight: 300;
      color: #94a3b8;
      padding-bottom: 0.1rem;
    }
  }

  ::v-deep .leave-type-select.v-select {
    .vs__dropdown-toggle {
      min-height: 44px;
      align-items: center;
    }

    .vs__selected-options {
      flex: 1 1 auto;
      flex-wrap: nowrap;
      overflow: hidden;
      align-items: center;
      min-width: 0;
    }

    .vs__selected {
      display: block !important;
      position: relative !important;
      opacity: 1 !important;
      visibility: visible !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      max-width: 100%;
      line-height: 1.35;
    }

    &.vs--single:not(.vs--open) .vs__selected {
      opacity: 1 !important;
    }

    &.vs--single.vs--open .vs__selected {
      opacity: 1 !important;
    }

    .vs__search {
      flex: 0 1 auto;
      min-width: 0;
      margin: 0;
      padding: 0;
    }

    .vs__dropdown-menu {
      min-width: 100% !important;
      padding-top: 0;
    }

    .vs__dropdown-option {
      padding: 0.55rem 0.75rem !important;
    }

    .vs__dropdown-option--highlight .lt-grid__name {
      color: #1e293b;
    }
  }

  .invalid-feedback {
    display: block;
    font-size: 0.875rem;
    color: #ef4444;
    margin-top: 0.25rem;
  }

  .form-text {
    font-size: 0.8125rem;
    color: #64748b;
    margin-top: 0.5rem;
  }
}

// Approve/Reject Modal Compact Styling
.approve-modal-compact,
.reject-modal-compact {
  ::v-deep .modal-dialog {
    max-width: 800px;
  }

  ::v-deep .modal-body {
    padding: 2rem;
    background: #ffffff;
  }
}
</style>
